import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from "react";
import {
  manualSpinStop,
  rgsPlay,
  setSpinComplete,
  setReelsFrozen,
  decrementRemainingAutoSpins,
  stopAutoPlay,
  setAutoplaySpeed,
  applyPendingAutoplaySpeed,
  incrementManualStopCount,
  setTurboSuggestionPending,
  setStopOnAnyWin,
  setStopOnBonusGame,
  setStopOnSingleWinExceeds,
  setStopOnBalanceIncreases,
  setStopOnBalanceDecreases,
  setSingleWinExceedsAmount,
  setBalanceIncreasesAmount,
  setBalanceDecreasesAmount,
  openConnectionPopup,
  closeConnectionPopup,
  setCounterAnimationFinished,
} from "../State/actions";
import {
  selectAutoPlaySpeed,
  selectAutoPlayActive,
  selectRemainingAutoSpins,
  selectPendingAutoPlaySpeed,
  selectAutoSpinCount,
  selectStopOnAnyWin,
  selectStopOnBonusGame,
  selectStopOnSingleWinExceeds,
  selectStopOnBalanceIncreases,
  selectStopOnBalanceDecreases,
  selectSingleWinExceedsAmount,
  selectBalanceIncreasesAmount,
  selectBalanceDecreasesAmount,
  selectCurrentRound,
  selectIsSpinning,
  selectManualStop,
  selectSpinComplete,
  selectMenuActive,
  selectBalance,
  selectBet,
  selectCounterAnimationFinished,
  selectCounterAnimationStarted,
  selectWinAnimationsPlaying,
  selectHoldAndWinActive,
  selectLastCommand,
  selectFeatureState,
  selectFlowState,
  selectGameStatus,
  selectErrorCode,
  selectErrorPopupActive,
  selectInfoPopupActive,
  selectFreeRoundsCompletionPending,
} from "../State/selectors";
import { useDispatch, useSelector } from "react-redux";
import store from "../State/Store/store";

import { FEATURES_CONFIG } from "../featuresConfig";
import { CONNECTION_POPUP_TIMEOUTS } from "../Components/UserInterface/primary/Popups/ConnectionPopup";
import { SOUND_NAME } from "../sounds";
import { useAppActivity } from "./AppActivityContext";
import {
  parseAutoplayInputFromStore,
  createPlayButtonRotationAnimation,
} from "../utilities";
import { Settings } from "../settings";
import { triggerScalePress } from "../Hooks/useButtonHover";
import { useReelRefs } from "./ReelRefsContext";
import { soundManager } from "../SoundManager";
import { COIN_SYMBOL_IDS } from "../SymbolData";

/**
 * Context for managing reel control functionality.
 * Handles manual spins, auto-play mode, spacebar turbo mode, and tension animations.
 */
export const ReelControlContext = createContext(null);

/**
 * Detects if the current round is a Hold & Win trigger (6+ coins).
 * Used to suppress landing animations in turbo mode even when there's no win amount.
 */
const detectHoldAndWinTrigger = (round) => {
  if (!round?.reels || !Array.isArray(round.reels)) return false;

  let coinCount = 0;

  for (let reelIndex = 0; reelIndex < round.reels.length; reelIndex++) {
    const reel = round.reels[reelIndex];
    if (!Array.isArray(reel)) continue;

    for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
      const symbolId = reel[symbolIndex];
      if (COIN_SYMBOL_IDS.includes(symbolId)) {
        coinCount++;
      }
    }
  }

  return coinCount >= 6;
};

export const ReelControlProvider = ({ children }) => {
  const { reelRefs, tensionReelRefs, playButtonRef, gameContainerRef } =
    useReelRefs();
  const dispatch = useDispatch();
  const { isActive } = useAppActivity();

  // ============================================================================
  // REFS & STATE MANAGEMENT
  // ============================================================================

  // Rate limiting for effects
  const lastActionRef = useRef({
    turboActivation: 0,
    turboContinuous: 0,
  });

  // Timeout tracking and spin state
  const timeoutsRef = useRef([]);
  const reelStopTimeoutsRef = useRef(new Map()); // Map<reelIndex, timeoutId> for tracking reel-specific timeouts
  const stopTriggeredRef = useRef(false);
  const pendingManualStopRef = useRef(false);
  const currentSpinMethodRef = useRef(null); // "turbo" | "regular" | null
  const stoppedReelsRef = useRef(new Set());
  const lastForceStoppedReelRef = useRef(-1); // Prevents duplicate force-stops on same reel
  const spinStartTimeRef = useRef(null);

  // Connection timeout management for slow server detection
  const connectionTimeouts = useRef({
    main: null,
    manualStop: null,
  });

  // Tension mode state (trigger symbol-based slow spin for anticipation)
  const tensionReels = useRef(new Set());
  const pendingTensionRef = useRef(null);
  const activeTensionAnimationsRef = useRef(new Set());
  const tensionAnimationStartTimesRef = useRef(new Map());
  const dimmedReelsRef = useRef(new Set()); // Track which reels are currently dimmed
  const tensionEffectActiveRef = useRef(false); // Track if tension effect animation is currently playing

  // Turbo mode state (spacebar-activated)
  const [spacebarTurboActive, setSpacebarTurboActive] = useState(false);
  const turboPendingStopRef = useRef(false);
  const turboPendingActivationRef = useRef(false);
  const previousSpeedRef = useRef("auto");
  const turboDeactivatingRef = useRef(false);
  const turboTensionEjectedRef = useRef(false); // True when turbo was ejected for tension — blocks held spacebar from skipping

  // Autoplay state management
  const autoplayProcessingRef = useRef(false);
  const autoplayInitializedRef = useRef(false);
  const pausedByInactivityRef = useRef(false);
  const lastTabReturnTimeRef = useRef(null); // Track last tab return to prevent rapid calls
  const responsibleGamblingCheckedRef = useRef(false);
  const autoplayStartBalanceRef = useRef(null); // Store balance when autoplay starts

  // Animation optimization flags
  const suppressLandingAnimationsRef = useRef(false);
  const suppressLandingAnimationsPerReelRef = useRef(new Set());
  const manualStopTargetReelsRef = useRef(new Set());

  // Cleanup guards
  const cleanupInProgressRef = useRef(false);
  const isSubscribedRef = useRef(false);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Clears all tracked timeouts (reel stops, autoplay delays, etc.)
   */
  const clearAllTimeouts = useCallback(() => {
    const timeouts = timeoutsRef.current;
    if (timeouts.length > 0) {
      for (let i = 0; i < timeouts.length; i++) {
        clearTimeout(timeouts[i]);
      }
      timeoutsRef.current = [];
    }
    reelStopTimeoutsRef.current.clear();
  }, []);

  /**
   * Clears connection timeout for regular spins (preserves manual stop timeout)
   */
  const clearConnectionTimeouts = useCallback(() => {
    if (connectionTimeouts.current.main) {
      clearTimeout(connectionTimeouts.current.main);
      // Remove from tracking array
      const idx = timeoutsRef.current.indexOf(connectionTimeouts.current.main);
      if (idx > -1) {
        timeoutsRef.current.splice(idx, 1);
      }
      connectionTimeouts.current.main = null;
    }
  }, []);

  /**
   * Clears connection timeout for manual stop operations
   */
  const clearManualStopTimeouts = useCallback(() => {
    if (connectionTimeouts.current.manualStop) {
      clearTimeout(connectionTimeouts.current.manualStop);
      // Remove from tracking array
      const idx = timeoutsRef.current.indexOf(
        connectionTimeouts.current.manualStop,
      );
      if (idx > -1) {
        timeoutsRef.current.splice(idx, 1);
      }
      connectionTimeouts.current.manualStop = null;
    }
  }, []);

  /**
   * Shows connection popup when server response is delayed
   */
  const showConnectionPopup = useCallback(() => {
    const currentState = store.getState();
    if (!currentState.userInterface.connectionPopupActive) {
      dispatch(openConnectionPopup());
    }
  }, [dispatch]);

  // ============================================================================
  // REDUX SELECTORS
  // ============================================================================

  const autoSpinCount = useSelector(selectAutoSpinCount);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const remainingAutoSpins = useSelector(selectRemainingAutoSpins);
  const pendingAutoPlaySpeed = useSelector(selectPendingAutoPlaySpeed);
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
  const isSpinning = useSelector(selectIsSpinning);
  const manualStop = useSelector(selectManualStop);
  const spinComplete = useSelector(selectSpinComplete);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const counterAnimationStarted = useSelector(selectCounterAnimationStarted);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const currentRound = useSelector(selectCurrentRound);
  const balance = useSelector(selectBalance);
  const bet = useSelector(selectBet);
  const menuActive = useSelector(selectMenuActive);
  const stopOnAnyWin = useSelector(selectStopOnAnyWin);
  const stopOnBonusGame = useSelector(selectStopOnBonusGame);
  const stopOnSingleWinExceeds = useSelector(selectStopOnSingleWinExceeds);
  const stopOnBalanceIncreases = useSelector(selectStopOnBalanceIncreases);
  const stopOnBalanceDecreases = useSelector(selectStopOnBalanceDecreases);
  const singleWinExceedsAmount = useSelector(selectSingleWinExceedsAmount);
  const balanceIncreasesAmount = useSelector(selectBalanceIncreasesAmount);
  const balanceDecreasesAmount = useSelector(selectBalanceDecreasesAmount);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  const lastCommand = useSelector(selectLastCommand);
  const errorPopupActive = useSelector(selectErrorPopupActive);
  const infoPopupActive = useSelector(selectInfoPopupActive);
  const freeRoundsCompletionPending = useSelector(
    selectFreeRoundsCompletionPending,
  );
  const featureState = useSelector(selectFeatureState);
  const flowState = useSelector(selectFlowState); // Legacy fallback
  const gameStatus = useSelector(selectGameStatus);
  const errorCode = useSelector(selectErrorCode);

  // ============================================================================
  // ERROR HANDLING - Stop autoplay on errors (except 205)
  // ============================================================================

  useEffect(() => {
    // Stop autoplay on any error (including error 205 free rounds)
    // This ensures autoplay exits cleanly when free rounds are triggered
    if (gameStatus === "failed" && errorCode && autoPlayActive) {
      dispatch(stopAutoPlay());
    }
  }, [gameStatus, errorCode, autoPlayActive, dispatch]);

  // ============================================================================
  // REEL CONTROL FUNCTIONS
  // ============================================================================

  /**
   * Reset all responsible gambling settings to clean state
   * Called when autoplay stops due to a responsible gambling condition being met
   * Must clear both checkboxes AND values since onChange handlers aren't called when dispatching directly
   */
  const resetResponsibleGamblingSettings = useCallback(() => {
    if (!FEATURES_CONFIG.RESPONSIBLE_GAMBLING.ENABLED) return;

    // Uncheck all checkboxes
    dispatch(setStopOnAnyWin(false));
    dispatch(setStopOnBonusGame(false));
    dispatch(setStopOnSingleWinExceeds(false));
    dispatch(setStopOnBalanceIncreases(false));
    dispatch(setStopOnBalanceDecreases(false));
    // Clear all input values
    dispatch(setSingleWinExceedsAmount(""));
    dispatch(setBalanceIncreasesAmount(""));
    dispatch(setBalanceDecreasesAmount(""));
  }, [dispatch]);

  /**
   * Stops all reels with timing based on spin mode and tension state.
   *
   * Four stop modes:
   *
   * 1. Turbo Mode:
   *    - All reels stop simultaneously (no delays)
   *    - Marks all reels as stopped immediately
   *    - Used when currentSpinMethodRef.current === "turbo"
   *
   * 2. Simultaneous Stop Mode (Manual Stop → Turbo):
   *    - All remaining reels stop simultaneously (no delays)
   *    - Used when switching to turbo mid-spin via manual stop
   *    - Provides instant stop feel for turbo activation
   *
   * 3. Normal Mode:
   *    - Sequential stops with base delay between each reel
   *    - Delay = delay * reelIndex (first reel stops immediately, last reel stops last)
   *    - Used for regular spins and autoplay
   *
   * 4. Tension Mode (trigger symbol-based):
   *    - Reels spin 3x slower than normal for anticipation
   *    - First N reels stop with normal timing
   *    - Reels within tension range use cumulative tension timing
   *    - Triggers tension animations and sounds
   *    - Cascades tension to next reel when each tension reel stops
   *    - CUTOFF: When 6 coins reached from stopped reels, remaining reels stop normally
   *
   * @param {number} delay - Base delay between reel stops (milliseconds)
   * @param {number|null} tensionStartReel - Index where tension mode should start (null = no tension)
   * @param {boolean} simultaneousStop - If true, all reels stop at once (no cascading delay)
   *
   * Clears all timeouts before starting to prevent conflicts.
   * Tracks all timeouts for proper cleanup.
   */
  const stopReels = useCallback(
    (delay, tensionStartReel = null, simultaneousStop = false) => {
      // Only clear reel stop timeouts, NOT reel start timeouts
      // This prevents clearing the reel start timeouts when API responds quickly
      reelStopTimeoutsRef.current.clear();

      // Check for per-mode reel stop delay override
      const currentState = store.getState();
      const currentSpeed = currentState.settings.autoPlaySpeed;
      const speedConfig =
        FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[currentSpeed?.toUpperCase()];
      const effectiveDelay =
        speedConfig?.REEL_STOP_DELAY !== undefined
          ? speedConfig.REEL_STOP_DELAY
          : delay;

      const isTensionMode = tensionStartReel !== null;
      const reels = reelRefs.current;

      // Unified stop path: speed config controls timing for all modes
      // Turbo without tension: REEL_STOP_DELAYS=[0,0,0,0,0] → all stop at once
      // Turbo with tension: non-tension reels stop at delay 0, tension reels get tension timing
      // Normal/Quick: sequential stops with configured delays
      // Sequential stops with calculated delays
      for (let index = 0; index < reels.length; index++) {
          const reel = reels[index];

          // Skip reels that have already stopped (prevents rescheduling on tab return)
          // BUT: Don't skip if tension is currently visible (reel is in tension mode)
          const reelController = reel?.getReelController?.();
          const isTensionVisible =
            tensionReelRefs.current[index]?.isTensionVisible();

          if (
            reelController &&
            reelController.hasStopped &&
            !isTensionVisible
          ) {
            continue;
          }

          // If reel is stopping but tension is visible, let it continue (it's in tension mode)
          if (
            reelController &&
            reelController.isStopping &&
            !isTensionVisible
          ) {
            continue;
          }

          let finalDelay;

          // Calculate delay based on mode
          if (simultaneousStop) {
            // Simultaneous stop mode: All reels stop at once (turbo switch)
            finalDelay = 0;
          } else if (isTensionMode && index >= tensionStartReel) {
            // Tension mode: Cumulative tension timing for anticipation
            // Read actual spine animation cycle duration (falls back to config)
            const tensionDuration =
              tensionReelRefs.current[
                tensionStartReel
              ]?.getTensionCycleDuration?.() ||
              FEATURES_CONFIG.TENSION.DURATION;
            const tensionReelIndex = index - tensionStartReel;
            const baseDelay = effectiveDelay * tensionStartReel;
            const cumulativeTensionDelay = tensionReelIndex * tensionDuration;
            finalDelay = baseDelay + cumulativeTensionDelay + tensionDuration;
          } else {
            // Normal mode: Non-linear cascading stops (gaps decrease for later reels)
            // Per-speed-mode delays take priority, then manual/auto global delays
            const isManualStop =
              effectiveDelay <= FEATURES_CONFIG.SPIN.STOP_DELAY_MANUAL;
            const reelStopDelays =
              speedConfig?.REEL_STOP_DELAYS ||
              (isManualStop
                ? FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_MANUAL
                : FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_AUTO);
            finalDelay = reelStopDelays
              ? reelStopDelays[index] || effectiveDelay * index
              : effectiveDelay * index;
          }

          const timeoutId = setTimeout(() => {
            // Remove this timeout from tracking since it fired naturally
            const idx = timeoutsRef.current.indexOf(timeoutId);
            if (idx > -1) {
              timeoutsRef.current.splice(idx, 1);
            }
            reelStopTimeoutsRef.current.delete(index);

            // Stop reel with appropriate method
            // Check if tension was canceled (cutoff reached) before stopping
            const tensionWasCanceled = !pendingTensionRef.current;
            if (
              isTensionMode &&
              index >= tensionStartReel &&
              !tensionWasCanceled
            ) {
              reel?.stopTensionSpin();
              // Play tension_out NOW (before bounce) so it fades during the landing animation
              tensionReelRefs.current[index]?.playTensionOut();
              activeTensionAnimationsRef.current.add(index);
              tensionAnimationStartTimesRef.current.set(index, Date.now());
            } else {
              // Mark the last non-tension reel so it plays the consolidated stop sound
              // (in turbo mode, the last reel normally plays it, but that reel is in tension)
              if (isTensionMode && index === tensionStartReel - 1) {
                const reelCtrl = reel?.getReelController?.();
                if (reelCtrl) {
                  reelCtrl.isLastNonTensionReel = true;
                }
              }
              reel?.stopSpin();
            }

            stoppedReelsRef.current.add(index);
          }, finalDelay);
          timeoutsRef.current.push(timeoutId);
          reelStopTimeoutsRef.current.set(index, timeoutId);

          // --- Register onReelLanded callback for tension triggering ---
          // This fires when the reel's bounce animation completes (after landing animations start),
          // giving perfect visual sync between the trigger symbol appearing and tension starting.
          reel?.setOnReelLanded?.((landedReelIndex) => {
            const tensionInfo = pendingTensionRef.current;
            if (!tensionInfo) return;

            // --- INITIAL TENSION TRIGGER ---
            // When the last trigger symbol reel lands, start tension on the first tension reel
            if (
              landedReelIndex === tensionInfo.lastTriggerSymbolReel &&
              landedReelIndex < tensionInfo.tensionStartReel
            ) {
              const firstTensionReel = tensionInfo.tensionStartReel;

              // Dim all reels ahead of the first tension reel
              for (
                let i = firstTensionReel + 1;
                i < reelRefs.current.length;
                i++
              ) {
                if (reelRefs.current[i]?.dimReel) {
                  reelRefs.current[i].dimReel();
                  dimmedReelsRef.current.add(i);
                }
              }

              soundManager.stop(SOUND_NAME.reel_spin);
              soundManager.stop(SOUND_NAME.tension);
              soundManager.play(SOUND_NAME.tension);

              // Start tension animation on the first tension reel
              const isAlreadyVisible =
                tensionReelRefs.current[firstTensionReel]?.isTensionVisible();
              const reelCtrl =
                reelRefs.current[firstTensionReel]?.getReelController?.();
              const reelHasStopped =
                reelCtrl?.hasStopped || reelCtrl?.isStopping;

              if (reelHasStopped && isAlreadyVisible) {
                tensionReelRefs.current[firstTensionReel]?.hideTension();
              } else if (!isAlreadyVisible && !reelHasStopped) {
                tensionReelRefs.current[firstTensionReel]?.playTensionIn();
              }

              // Start tension zoom effect (only once per spin, only if HoldAndWin is not active)
              if (!holdAndWinActive && !tensionEffectActiveRef.current) {
                // Calculate zoom duration based on number of tension reels
                const tensionReelCount =
                  reelRefs.current.length - firstTensionReel;
                const tensionCycleDuration =
                  tensionReelRefs.current[
                    firstTensionReel
                  ]?.getTensionCycleDuration?.() ||
                  FEATURES_CONFIG.TENSION.DURATION;
                const zoomDuration = tensionReelCount * tensionCycleDuration;
                gameContainerRef?.current?.playTensionEffect?.(zoomDuration);
                tensionEffectActiveRef.current = true;
              }

              // Start tension spin on first tension reel
              if (reelRefs.current[firstTensionReel]) {
                if (reelCtrl && !reelCtrl.isStopping && !reelCtrl.hasStopped) {
                  reelRefs.current[firstTensionReel].startTensionSpin();
                  if (dimmedReelsRef.current.has(firstTensionReel)) {
                    reelRefs.current[firstTensionReel]?.undimReel();
                    dimmedReelsRef.current.delete(firstTensionReel);
                  }
                }
              }
            }
            // --- EDGE CASE: trigger reel IS the first tension reel ---
            else if (
              landedReelIndex === tensionInfo.lastTriggerSymbolReel &&
              landedReelIndex >= tensionInfo.tensionStartReel
            ) {
              const firstTensionReel = tensionInfo.tensionStartReel;
              const reelCtrl =
                reelRefs.current[firstTensionReel]?.getReelController?.();
              if (reelCtrl && !reelCtrl.isStopping && !reelCtrl.hasStopped) {
                soundManager.stop(SOUND_NAME.reel_spin);
                soundManager.stop(SOUND_NAME.tension);
                soundManager.play(SOUND_NAME.tension);

                const isAlreadyVisible =
                  tensionReelRefs.current[firstTensionReel]?.isTensionVisible();
                const reelHasStopped =
                  reelCtrl?.hasStopped || reelCtrl?.isStopping;

                if (reelHasStopped && isAlreadyVisible) {
                  tensionReelRefs.current[firstTensionReel]?.hideTension();
                } else if (!isAlreadyVisible && !reelHasStopped) {
                  tensionReelRefs.current[firstTensionReel]?.playTensionIn();
                }

                if (!holdAndWinActive && !tensionEffectActiveRef.current) {
                  // Calculate zoom duration based on number of tension reels
                  const tensionReelCount =
                    reelRefs.current.length - firstTensionReel;
                  const tensionCycleDuration =
                    tensionReelRefs.current[
                      firstTensionReel
                    ]?.getTensionCycleDuration?.() ||
                    FEATURES_CONFIG.TENSION.DURATION;
                  const zoomDuration = tensionReelCount * tensionCycleDuration;
                  gameContainerRef?.current?.playTensionEffect?.(zoomDuration);
                  tensionEffectActiveRef.current = true;
                }

                reelRefs.current[firstTensionReel].startTensionSpin();
              }
            }

            // --- TENSION CASCADE: chain tension from reel to reel ---
            // When a tension reel lands, start tension on the next reel
            // (tension_out already started in the stopSpin timer, before the bounce)
            const tensionWasCanceled = !pendingTensionRef.current;
            if (
              isTensionMode &&
              landedReelIndex >= tensionStartReel &&
              !tensionWasCanceled
            ) {
              // Check cutoff threshold
              const tensionInfoCurrent = pendingTensionRef.current;
              if (tensionInfoCurrent && tensionInfoCurrent.cutoffThreshold) {
                // Use pre-computed scatter positions from RGS adapter
                const currentState = store.getState();
                const currentRound = currentState?.game?.currentRound;
                const scatterPositions = currentRound?.scatterPositions || [];

                // Count total scatters on already-landed reels (not unique reels)
                // e.g. reel 0 has 1 scatter + reel 1 has 2 scatters = 3 total → hits cutoff
                const currentCoinCount = scatterPositions
                  .filter(([reelIndex]) => reelIndex <= landedReelIndex)
                  .length;

                // If cutoff threshold reached, cancel tension for remaining reels
                // Stop them with manual-stop-speed delays (fast cascade, no lingering)
                const nextReelIndex = landedReelIndex + 1;
                if (
                  currentCoinCount >= tensionInfoCurrent.cutoffThreshold &&
                  nextReelIndex < reelRefs.current.length
                ) {
                  // Use manual stop delays for fast cascading stops
                  const manualDelays =
                    FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_MANUAL;
                  const manualDelay = FEATURES_CONFIG.SPIN.STOP_DELAY_MANUAL;

                  for (
                    let i = nextReelIndex;
                    i < reelRefs.current.length;
                    i++
                  ) {
                    const existingTimeout = reelStopTimeoutsRef.current.get(i);
                    if (existingTimeout) {
                      clearTimeout(existingTimeout);
                      reelStopTimeoutsRef.current.delete(i);
                      const timeoutIdx =
                        timeoutsRef.current.indexOf(existingTimeout);
                      if (timeoutIdx > -1) {
                        timeoutsRef.current.splice(timeoutIdx, 1);
                      }
                    }

                    // Calculate delay relative to cutoff reel (same as manual stop cascade)
                    const reelOffset = i - nextReelIndex;
                    const fastDelay = manualDelays
                      ? manualDelays[reelOffset] || manualDelay * reelOffset
                      : manualDelay * reelOffset;

                    const newTimeoutId = setTimeout(() => {
                      const tidx = timeoutsRef.current.indexOf(newTimeoutId);
                      if (tidx > -1) {
                        timeoutsRef.current.splice(tidx, 1);
                      }
                      reelStopTimeoutsRef.current.delete(i);

                      reelRefs.current[i]?.stopSpin();
                      stoppedReelsRef.current.add(i);
                    }, fastDelay);

                    timeoutsRef.current.push(newTimeoutId);
                    reelStopTimeoutsRef.current.set(i, newTimeoutId);

                    if (dimmedReelsRef.current.has(i)) {
                      reelRefs.current[i]?.undimReel();
                      dimmedReelsRef.current.delete(i);
                    }
                    tensionReelRefs.current[i]?.forceStopTension();
                    tensionReels.current.delete(i);
                  }

                  pendingTensionRef.current = null;
                  return;
                }
              }

              // Start tension on next reel (only if cutoff not reached)
              const nextReelIndex = landedReelIndex + 1;
              if (nextReelIndex < reelRefs.current.length) {
                soundManager.stop(SOUND_NAME.reel_spin);
                soundManager.stop(SOUND_NAME.tension);
                soundManager.play(SOUND_NAME.tension);

                const nextReelController =
                  reelRefs.current[nextReelIndex]?.getReelController?.();
                const nextReelHasStopped =
                  nextReelController?.hasStopped ||
                  nextReelController?.isStopping;
                const isAlreadyVisible =
                  tensionReelRefs.current[nextReelIndex]?.isTensionVisible();

                if (nextReelHasStopped && isAlreadyVisible) {
                  tensionReelRefs.current[nextReelIndex]?.hideTension();
                } else if (!isAlreadyVisible && !nextReelHasStopped) {
                  tensionReelRefs.current[nextReelIndex]?.playTensionIn();
                }

                if (reelRefs.current[nextReelIndex]) {
                  const reelCtrl =
                    reelRefs.current[nextReelIndex].getReelController?.();
                  if (
                    reelCtrl &&
                    !reelCtrl.isStopping &&
                    !reelCtrl.hasStopped
                  ) {
                    reelRefs.current[nextReelIndex].startTensionSpin();
                    if (dimmedReelsRef.current.has(nextReelIndex)) {
                      reelRefs.current[nextReelIndex]?.undimReel();
                      dimmedReelsRef.current.delete(nextReelIndex);
                    }
                  }
                }
              }
            }
          });
        }


      currentSpinMethodRef.current = null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearAllTimeouts, dispatch],
  );

  /**
   * Freezes all reels instantly at their current position
   * Used for error popups (e.g., error 205 free rounds) where reels should freeze mid-spin
   * No landing animations, no sound effects, just instant freeze
   */
  const freezeReels = useCallback(() => {
    const reels = reelRefs.current;

    // Freeze all reels instantly
    for (let i = 0; i < reels.length; i++) {
      reels[i]?.freezeSpin();
    }

    // Stop all sounds
    soundManager.stop(SOUND_NAME.reel_spin);
    soundManager.stop(SOUND_NAME.tension);

    // Clear all timeouts
    clearAllTimeouts();

    // Reset state flags
    stopTriggeredRef.current = true;
    pendingManualStopRef.current = false;
    pendingTensionRef.current = null;
    tensionReels.current = new Set();
    activeTensionAnimationsRef.current.clear();
    tensionAnimationStartTimesRef.current.clear();
    dimmedReelsRef.current.clear();
    tensionEffectActiveRef.current = false;

    // Undim all reels
    for (let i = 0; i < reelRefs.current.length; i++) {
      reelRefs.current[i]?.undimReel();
    }

    // Stop tension effect animation
    gameContainerRef?.current?.stopTensionEffect?.();

    // Mark reels as frozen (need recovery)
    dispatch(setReelsFrozen(true));
  }, [reelRefs, clearAllTimeouts, gameContainerRef, dispatch]);

  /**
   * Restores all reels to idle state with specific symbols
   * Used after error recovery to show previous round's symbols properly
   * Called during error recovery transition overlay
   * @param {Array} reelsData - Array of symbol IDs for each reel
   * @param {number|null} betValue - Optional bet value to use for coin texts (for error recovery)
   */
  const restoreReels = useCallback(
    (reelsData, betValue = null) => {
      if (!reelsData || !Array.isArray(reelsData)) {
        return;
      }

      const reels = reelRefs.current;

      // Restore each reel to idle state with its symbols
      for (let i = 0; i < reels.length && i < reelsData.length; i++) {
        const symbolIds = reelsData[i];
        if (symbolIds && Array.isArray(symbolIds)) {
          reels[i]?.restoreToIdleState(symbolIds, betValue);
        }
      }

      // Reset all state flags
      stopTriggeredRef.current = false;
      pendingManualStopRef.current = false;
      pendingTensionRef.current = null;
      tensionReels.current = new Set();
      activeTensionAnimationsRef.current.clear();
      tensionAnimationStartTimesRef.current.clear();
      dimmedReelsRef.current.clear();
      tensionEffectActiveRef.current = false;
    },
    [reelRefs],
  );

  /**
   * Emergency Tension State Cleanup Effect
   * Prevents memory leaks from orphaned tension state if a spin completes unexpectedly.
   * Runs every 10 seconds to detect and clean up any tension state that wasn't properly cleared.
   * Only cleans up when no spin is active to avoid interfering with active gameplay.
   */
  useEffect(() => {
    const emergencyCleanup = () => {
      if (pendingTensionRef.current && !isSpinning) {
        pendingTensionRef.current = null;
        tensionReels.current = new Set();
        stoppedReelsRef.current.clear();
        lastForceStoppedReelRef.current = -1;
        activeTensionAnimationsRef.current.clear();
        tensionAnimationStartTimesRef.current.clear();
        // Don't stop shake animation here - let it play until spin completes
      }
    };

    const cleanupInterval = setInterval(emergencyCleanup, 10000);

    return () => {
      clearInterval(cleanupInterval);
      emergencyCleanup();
    };
  }, [isSpinning, gameContainerRef]);

  /**
   * API Response Detection & Reel Stopping Effect
   *
   * Primary authority for stopping reels based on API responses. Subscribes to Redux store
   * changes when a spin is active and handles two scenarios:
   *
   * 1. Regular Spin Flow:
   *    - Sets connection timeout based on expected spin duration (turbo/quick/auto)
   *    - Waits for successful API response with reel data
   *    - Detects trigger symbols (coin/scatter based on config) for tension mode activation
   *    - Applies speed-specific animation optimizations (suppresses landing animations in turbo)
   *    - Calculates minimum spin duration and schedules reel stops accordingly
   *    - Manages connection timeout popups for slow server responses
   *
   * 2. Manual Stop Flow:
   *    - Subscribes separately when manual stop is triggered
   *    - Waits for API response to arrive
   *    - Applies per-reel landing animation suppression based on win presence
   *    - Stops reels with manual stop timing (faster than auto)
   *
   * Handles fake spin mode (insufficient credits) and cleans up subscriptions properly.
   */
  useEffect(() => {
    let intervalId;
    let unsubscribe;
    let unsubManualCleanup;
    let triggered = false;

    const shouldSubscribe = isSpinning && !manualStop && !spinComplete;


    if (shouldSubscribe) {
      if (isSubscribedRef.current) {
        return;
      }
      isSubscribedRef.current = true;

      // Set connection timeout based on expected spin duration
      const currentState = store.getState();
      const currentAutoPlaySpeed = currentState.settings.autoPlaySpeed;
      let expectedDuration;

      if (currentAutoPlaySpeed === "turbo") {
        expectedDuration =
          FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO.MIN_SPIN_DURATION;
      } else if (currentAutoPlaySpeed === "quick") {
        expectedDuration =
          FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.QUICK.SPIN_DURATION;
      } else {
        expectedDuration =
          FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO.SPIN_DURATION;
      }

      const naturalSpinDelay = CONNECTION_POPUP_TIMEOUTS.NATURAL_SPIN_DELAY;
      connectionTimeouts.current.main = setTimeout(() => {
        if (!triggered) {
          showConnectionPopup();
        }
      }, expectedDuration + naturalSpinDelay);
      // Also track in main timeouts array for proper cleanup
      timeoutsRef.current.push(connectionTimeouts.current.main);

      const onStoreChange = () => {
        if (triggered) return;

        const state = store.getState();
        const currentRound = state.game.currentRound;
        const currentStatus = state.spin.status;

        // Check for ANY error FIRST (before checking for reels)
        // All errors now freeze reels instead of landing on fake patterns
        // Catches both structured errors (with errorCode) and generic network failures
        if (currentStatus === "failed") {
          triggered = true;
          clearConnectionTimeouts();

          const uiState = store.getState();
          if (uiState.userInterface.connectionPopupActive) {
            dispatch(closeConnectionPopup());
          }

          // Freeze all reels instantly (no landing animation)
          requestAnimationFrame(() => {
            freezeReels();
          });

          if (unsubscribe) unsubscribe();
          return;
        }

        // Handle successful spins - proceed to stop reels normally
        if (currentStatus === "succeeded" && currentRound?.reels?.length) {
          // API responded successfully, proceed to stop reels normally
        } else {
          return; // Wait for API response with reel data
        }

        triggered = true;
        clearConnectionTimeouts();

        const uiState = store.getState();
        if (uiState.userInterface.connectionPopupActive) {
          dispatch(closeConnectionPopup());
        }

        const currentAutoPlaySpeed = state.settings.autoPlaySpeed;
        const reels = currentRound.reels;

        // Handle fake spin mode
        if (state.spin.fakeSpinMode) {
          requestAnimationFrame(() => {
            stopReels(FEATURES_CONFIG.SPIN.STOP_DELAY_AUTO, null);
          });
          if (unsubscribe) unsubscribe();
          return;
        }

        // Check for wins and apply speed-specific optimizations
        const hasStructuredWins =
          currentRound?.wins && Object.keys(currentRound.wins).length > 0;
        const hasAmountWin = !!currentRound?.win && currentRound.win > 0;
        const hasWin = hasStructuredWins || hasAmountWin;

        // Check for Hold & Win trigger (6+ coins) - even if no win amount
        const isHoldAndWinTrigger = detectHoldAndWinTrigger(currentRound);

        // Speed-specific animation optimizations
        if (currentAutoPlaySpeed === "turbo") {
          // Turbo: per-reel landing suppression — only for non-tension reels
          // Tension reels need landing animations for the cascade visual
          if (hasWin || isHoldAndWinTrigger) {
            suppressLandingAnimationsRef.current = true;
          }
          // NOTE: If tension is detected below, we'll narrow this to non-tension reels only
        } else if (
          currentAutoPlaySpeed === "auto" ||
          currentAutoPlaySpeed === "quick"
        ) {
          if (hasWin) {
            suppressLandingAnimationsPerReelRef.current.add(4);
          } else {
            suppressLandingAnimationsPerReelRef.current.delete(4);
          }
        } else {
          suppressLandingAnimationsPerReelRef.current.delete(3);
          suppressLandingAnimationsPerReelRef.current.delete(4);
        }

        // Detect trigger symbols (coin/scatter) for tension mode
        // Uses pre-computed scatterCount and scatterPositions from RGS adapter
        let shouldApplyTension = null;

        if (FEATURES_CONFIG.TENSION.ENABLED) {
          const minSymbols = FEATURES_CONFIG.TENSION.MIN_SYMBOLS;
          const cutoffThreshold = FEATURES_CONFIG.TENSION.CUTOFF_THRESHOLD;

          // Read pre-computed scatter data from currentRound (set by RGS adapter)
          const coinCount = currentRound.scatterCount || 0;
          const scatterPositions = currentRound.scatterPositions || [];


          // Build per-reel scatter counts (handles multiple scatters/coins per reel)
          const scatterCountByReel = new Map();
          for (const [r] of scatterPositions) {
            scatterCountByReel.set(r, (scatterCountByReel.get(r) || 0) + 1);
          }
          // Get sorted unique reel indices with scatters
          const sortedScatterReels = [...scatterCountByReel.keys()].sort((a, b) => a - b);

          if (coinCount >= minSymbols) {
            // Walk reels left-to-right, accumulating scatter count
            // When cumulative count reaches minSymbols, tension starts on the NEXT reel
            let cumulativeCount = 0;
            let tensionStartReel = -1;
            let lastTriggerSymbolReel = -1;

            for (let i = 0; i < sortedScatterReels.length; i++) {
              const reelIdx = sortedScatterReels[i];
              cumulativeCount += scatterCountByReel.get(reelIdx);
              if (cumulativeCount >= minSymbols && tensionStartReel === -1) {
                lastTriggerSymbolReel = reelIdx;
                tensionStartReel = lastTriggerSymbolReel + 1; // Start tension on reel right after last trigger scatter
              }
            }

            // Apply tension if threshold reached and tension reel is within bounds
            if (tensionStartReel !== -1 && tensionStartReel < reels.length) {
              shouldApplyTension = tensionStartReel;

              // Guard: Only set pendingTensionRef if not already set
              if (!pendingTensionRef.current) {
                pendingTensionRef.current = {
                  tensionStartReel: shouldApplyTension,
                  coinCount: coinCount,
                  lastTriggerSymbolReel: lastTriggerSymbolReel,
                  cutoffThreshold: cutoffThreshold,
                };
                const newTensionReels = new Set();
                for (let i = shouldApplyTension; i < reels.length; i++) {
                  newTensionReels.add(i);
                }
                tensionReels.current = newTensionReels;
              } else {
                // Tension already triggered, just use the existing value
                shouldApplyTension = pendingTensionRef.current.tensionStartReel;
              }
            }
          }
        }

        // Turbo + tension: narrow landing suppression to non-tension reels only
        // Tension reels need landing animations for the cascade (trigger spine, dimming, etc.)
        if (currentAutoPlaySpeed === "turbo" && shouldApplyTension !== null && suppressLandingAnimationsRef.current) {
          suppressLandingAnimationsRef.current = false;
          for (let i = 0; i < shouldApplyTension; i++) {
            suppressLandingAnimationsPerReelRef.current.add(i);
          }
          // Mark ALL reel controllers so scatter symbols get landing→idle even on pre-tension reels
          for (let i = 0; i < reelRefs.current.length; i++) {
            const ctrl = reelRefs.current[i]?.getReelController?.();
            if (ctrl) ctrl.hasTensionThisSpin = true;
          }
        }

        // Turbo + tension: eject spacebar turbo hold so held spacebar doesn't auto-skip tension
        // Only for spacebar-driven turbo — autoplay turbo has no held key to eject
        // Speed stays "turbo" — only the hold is forcefully lifted, NOT the speed setting
        if (currentAutoPlaySpeed === "turbo" && spacebarTurboActive && shouldApplyTension !== null) {
          setSpacebarTurboActive(false);
          turboPendingActivationRef.current = false;
          // Block held spacebar from auto-skipping tension reels
          turboTensionEjectedRef.current = true;
          // Defer speed restoration to spin complete — prevents speed getting stuck at "turbo"
          turboPendingStopRef.current = true;
        }

        // Centralized stop command
        const mode = state.settings.autoPlaySpeed;
        let minSpinDuration;
        if (mode === "turbo") {
          minSpinDuration =
            FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO.MIN_SPIN_DURATION;
        } else if (mode === "quick") {
          minSpinDuration =
            FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.QUICK.SPIN_DURATION;
        } else {
          minSpinDuration =
            FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO.SPIN_DURATION;
        }
        const now = Date.now();
        const startTime = spinStartTimeRef.current || 0;
        const elapsed = now - startTime;
        const remaining = minSpinDuration - elapsed;

        if (remaining > 0) {
          const timeoutId = setTimeout(() => {
            // Remove this timeout from tracking since it fired naturally
            const idx = timeoutsRef.current.indexOf(timeoutId);
            if (idx > -1) {
              timeoutsRef.current.splice(idx, 1);
            }
            stopReels(FEATURES_CONFIG.SPIN.STOP_DELAY_AUTO, shouldApplyTension);
          }, remaining);
          timeoutsRef.current.push(timeoutId);
        } else {
          stopReels(FEATURES_CONFIG.SPIN.STOP_DELAY_AUTO, shouldApplyTension);
        }

        if (unsubscribe) unsubscribe();
      };

      unsubscribe = store.subscribe(onStoreChange);
      // Immediate check in case status already succeeded before subscription
      onStoreChange();
    } else if (manualStop && pendingManualStopRef.current) {
      // Prevent duplicate subscriptions if already subscribed
      if (isSubscribedRef.current) {
        return;
      }
      isSubscribedRef.current = true;

      // Manual stop: subscribe once to detect when API response arrives
      const onManualStoreChange = () => {
        const state = store.getState();

        // Handle errors during manual stop — freeze reels and clean up
        if (state.spin.status === "failed") {
          pendingManualStopRef.current = false;
          spinStartTimeRef.current = null;

          // Clear manual stop connection timeouts and hide popup if shown
          clearManualStopTimeouts();
          const uiState = store.getState();
          if (uiState.userInterface.connectionPopupActive) {
            dispatch(closeConnectionPopup());
          }

          // Freeze all reels instantly
          requestAnimationFrame(() => {
            freezeReels();
          });

          if (unsubManualCleanup) unsubManualCleanup();
          return;
        }

        if (state.spin.status !== "succeeded") return;

        pendingManualStopRef.current = false;
        spinStartTimeRef.current = null;

        // Clear manual stop connection timeouts and hide popup if shown
        clearManualStopTimeouts();
        const uiState = store.getState();
        if (uiState.userInterface.connectionPopupActive) {
          dispatch(closeConnectionPopup());
        }

        // Decide per-reel landing suppression for manual stop based on win presence
        try {
          const round = state?.game?.currentRound;
          const hasStructuredWins =
            round?.wins && Object.keys(round.wins).length > 0;
          const hasAmountWin = !!round?.win && round.win > 0;
          const hasWin = hasStructuredWins || hasAmountWin;

          // Check for Hold & Win trigger (6+ coins) - even if no win amount
          const isHoldAndWinTrigger = detectHoldAndWinTrigger(round);

          // Suppress landing animations for wins OR Hold & Win triggers
          if (hasWin || isHoldAndWinTrigger) {
            suppressLandingAnimationsPerReelRef.current = new Set(
              manualStopTargetReelsRef.current,
            );
          } else {
            suppressLandingAnimationsPerReelRef.current.clear();
          }
        } catch (e) {
          suppressLandingAnimationsPerReelRef.current.clear();
        }

        // Clear snapshot to avoid leaking to next spin
        manualStopTargetReelsRef.current.clear();

        // Check if switching to turbo mode - if so, stop all reels simultaneously
        const isSwitchingToTurbo =
          state.settings.pendingAutoPlaySpeed === "turbo";

        // Compute tension for manual stop — pre-tension reels stop fast,
        // but tension reels still get the full tension animation
        let manualStopTension = null;
        if (FEATURES_CONFIG.TENSION.ENABLED && !isSwitchingToTurbo) {
          const round = state?.game?.currentRound;
          const minSymbols = FEATURES_CONFIG.TENSION.MIN_SYMBOLS;
          const cutoffThreshold = FEATURES_CONFIG.TENSION.CUTOFF_THRESHOLD;
          const coinCount = round?.scatterCount || 0;
          const scatterPositions = round?.scatterPositions || [];
          // Build per-reel scatter counts (handles multiple scatters/coins per reel)
          const scatterCountByReel = new Map();
          for (const [r] of scatterPositions) {
            scatterCountByReel.set(r, (scatterCountByReel.get(r) || 0) + 1);
          }
          const sortedScatterReels = [...scatterCountByReel.keys()].sort((a, b) => a - b);

          if (coinCount >= minSymbols) {
            let tensionStartReel = -1;
            let lastTriggerSymbolReel = -1;
            let cumulativeCount = 0;
            for (let i = 0; i < sortedScatterReels.length; i++) {
              const reelIdx = sortedScatterReels[i];
              cumulativeCount += scatterCountByReel.get(reelIdx);
              if (cumulativeCount >= minSymbols && tensionStartReel === -1) {
                lastTriggerSymbolReel = reelIdx;
                tensionStartReel = lastTriggerSymbolReel + 1;
              }
            }

            if (
              tensionStartReel !== -1 &&
              tensionStartReel < reelRefs.current.length
            ) {
              manualStopTension = tensionStartReel;

              // Set pendingTensionRef if not already set (needed for tension animations)
              if (!pendingTensionRef.current) {
                pendingTensionRef.current = {
                  tensionStartReel: manualStopTension,
                  coinCount,
                  lastTriggerSymbolReel,
                  cutoffThreshold,
                };
                const newTensionReels = new Set();
                for (
                  let i = manualStopTension;
                  i < reelRefs.current.length;
                  i++
                ) {
                  newTensionReels.add(i);
                }
                tensionReels.current = newTensionReels;
              }
            }
          }
        }

        stopReels(
          FEATURES_CONFIG.SPIN.STOP_DELAY_MANUAL,
          manualStopTension,
          isSwitchingToTurbo,
        );

        // Clear any pending forceStopTension hide timeouts from handleManualStop.
        // The onReelLanded callback (registered by stopReels) handles the visual
        // trigger when the last trigger reel lands — we just need to prevent
        // stale timeouts from interfering.
        if (manualStopTension !== null) {
          for (
            let i = manualStopTension;
            i < tensionReelRefs.current.length;
            i++
          ) {
            tensionReelRefs.current[i]?.clearAllTimeouts();
          }
        }

        if (unsubManualCleanup) unsubManualCleanup();
      };

      unsubManualCleanup = store.subscribe(onManualStoreChange);
      // Immediate check in case response already arrived
      onManualStoreChange();
    }

    return () => {
      // Reset subscription guard flag
      if (isSubscribedRef.current) {
        isSubscribedRef.current = false;
      }

      if (intervalId) {
        clearInterval(intervalId);
      }
      if (unsubscribe) {
        unsubscribe();
      }
      // Ensure manual-stop subscription is also cleaned up if still active
      if (typeof unsubManualCleanup === "function") {
        try {
          unsubManualCleanup();
        } catch (e) {}
      }

      // Enhanced cleanup - clear main connection timeout
      clearConnectionTimeouts();

      // Only close popup if we're unmounting the component entirely
      // Don't close during normal state transitions (like manual stop)
      const currentState = store.getState();
      const isUnmounting =
        !currentState.spin.isSpinning &&
        !currentState.spin.manualStop &&
        currentState.spin.spinComplete;

      if (!triggered && isUnmounting) {
        const uiState = store.getState();
        if (uiState.userInterface.connectionPopupActive) {
          dispatch(closeConnectionPopup());
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSpinning,
    manualStop,
    spinComplete,
    stopReels,
    freezeReels,
    dispatch,
    clearConnectionTimeouts,
    clearManualStopTimeouts,
    showConnectionPopup,
    reelRefs,
  ]);

  // ============================================================================
  // AUTOPLAY STATE MEMOIZATION
  // ============================================================================

  /**
   * Memoized Autoplay State
   *
   * Consolidates all autoplay-related Redux selectors into a single memoized object.
   * Prevents unnecessary re-renders of the Autoplay Continuous Spinning Effect by
   * ensuring the effect only runs when autoplay-related state actually changes.
   *
   * Includes: autoplay active state, spin completion, remaining spins, balance, bet,
   * counter animation state, autoplay speed, win animations, current round, menu state.
   */
  const autoplayState = useMemo(() => {
    return {
      autoPlayActive,
      spinComplete,
      remainingAutoSpins,
      balance,
      bet,
      counterAnimationFinished,
      // autoPlaySpeed removed - read directly from store to prevent timeout resets on speed changes
      winAnimationsPlaying,
      currentRound,
      menuActive,
      holdAndWinActive,
      lastCommand,
      featureState,
      flowState,
      stopOnAnyWin,
      stopOnBonusGame,
      stopOnSingleWinExceeds,
      stopOnBalanceIncreases,
      stopOnBalanceDecreases,
      singleWinExceedsAmount,
      balanceIncreasesAmount,
      balanceDecreasesAmount,
      errorPopupActive,
      infoPopupActive,
      freeRoundsCompletionPending,
    };
  }, [
    autoPlayActive,
    spinComplete,
    remainingAutoSpins,
    balance,
    bet,
    counterAnimationFinished,
    // autoPlaySpeed removed from dependencies
    winAnimationsPlaying,
    currentRound,
    menuActive,
    holdAndWinActive,
    lastCommand,
    featureState,
    flowState,
    stopOnAnyWin,
    stopOnBonusGame,
    stopOnSingleWinExceeds,
    stopOnBalanceIncreases,
    stopOnBalanceDecreases,
    singleWinExceedsAmount,
    balanceIncreasesAmount,
    balanceDecreasesAmount,
    errorPopupActive,
    infoPopupActive,
    freeRoundsCompletionPending,
  ]);

  // ============================================================================
  // USER ACTION HANDLERS
  // ============================================================================

  /**
   * Handles manual stop action triggered by user click/spacebar/touch.
   *
   * Performs comprehensive cleanup and state management:
   * - Validates state guards (UI visibility, win animations)
   * - Checks if any reels are still winding up (defers stop until all wind-ups complete)
   * - Tracks manual stop count for turbo suggestion feature (suggests turbo after 4 stops)
   * - Sets up connection timeout popup for slow server responses
   * - Snapshot which reels haven't stopped yet for animation suppression
   * - Stops all tension animations and sounds immediately
   * - Clears all tracking state (stopped reels, tension animations, timestamps)
   * - Dispatches manual stop and autoplay stop actions
   * - Deactivates spacebar turbo if active
   *
   * Prevents duplicate stops using stopTriggeredRef guard.
   */
  const handleManualStop = useCallback(() => {
    const currentState = store.getState();

    if (currentState.userInterface.userInterfaceHidden) {
      return;
    }

    if (currentState.animations.winAnimationsPlaying) {
      return;
    }

    // --- TENSION SKIP: press during active tension skips current reel only ---
    // Each press skips one tension reel. The cascade naturally starts the next.
    if (pendingTensionRef.current) {
      // Find the currently active tension reel (has visible tension spine)
      let activeTensionReel = -1;
      for (let i = 0; i < tensionReelRefs.current.length; i++) {
        if (tensionReelRefs.current[i]?.isTensionVisible()) {
          activeTensionReel = i;
          break;
        }
      }

      if (activeTensionReel !== -1) {
        // Skip if we already force-stopped this same reel (rapid press guard)
        if (activeTensionReel === lastForceStoppedReelRef.current) {
          return;
        }
        lastForceStoppedReelRef.current = activeTensionReel;

        // Cancel this reel's pending stop timeout (it would fire after tensionDuration)
        const existingTimeout =
          reelStopTimeoutsRef.current.get(activeTensionReel);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          reelStopTimeoutsRef.current.delete(activeTensionReel);
          const timeoutIdx = timeoutsRef.current.indexOf(existingTimeout);
          if (timeoutIdx > -1) {
            timeoutsRef.current.splice(timeoutIdx, 1);
          }
        }

        // Force-stop this tension reel: plays tension_out and stops the reel
        reelRefs.current[activeTensionReel]?.stopTensionSpin();
        tensionReelRefs.current[activeTensionReel]?.playTensionOut();
        activeTensionAnimationsRef.current.add(activeTensionReel);
        tensionAnimationStartTimesRef.current.set(
          activeTensionReel,
          Date.now(),
        );
        stoppedReelsRef.current.add(activeTensionReel);

        return; // Don't do full manual stop — just skip this reel
      }
    }

    if (stopTriggeredRef.current) {
      return;
    }

    // CRITICAL: Prevent duplicate manual stops during autoplay
    // If autoplay is active and manual stop is already pending, ignore duplicate calls
    if (currentState.settings.autoPlayActive && pendingManualStopRef.current) {
      return;
    }

    // Check if any reel is still winding up
    // If so, defer manual stop until all wind-ups complete
    const reels = reelRefs.current;
    let anyReelWindingUp = false;
    for (let i = 0; i < reels.length; i++) {
      const controller = reels[i]?.getReelController();
      if (controller?.isWindingUp) {
        anyReelWindingUp = true;
        break;
      }
    }

    // If any reel is winding up, just set pending flag and wait
    if (anyReelWindingUp) {
      pendingManualStopRef.current = true;
      stopTriggeredRef.current = true;
      return; // Don't dispatch yet, let wind-ups complete
    }

    stopTriggeredRef.current = true;
    pendingManualStopRef.current = true;
    spinStartTimeRef.current = null;

    // Track manual stops for turbo suggestion feature (only if feature is enabled)
    if (FEATURES_CONFIG.TURBO_SUGGESTION.ENABLED) {
      const settingsState = store.getState().settings;
      if (settingsState.manualStopTrackingEnabled) {
        dispatch(incrementManualStopCount());
        if (settingsState.manualStopCount >= 4) {
          dispatch(setTurboSuggestionPending(true));
        }
      }
    }

    // Set connection timeout for manual stop
    const manualStopBuffer = CONNECTION_POPUP_TIMEOUTS.MANUAL_STOP_BUFFER;
    connectionTimeouts.current.manualStop = setTimeout(() => {
      const recheckState = store.getState();
      const stillNoData =
        recheckState.spin.status !== "succeeded" ||
        !recheckState.game.currentRound?.reels?.length;

      if (
        stillNoData &&
        recheckState.spin.manualStop &&
        recheckState.spin.isSpinning
      ) {
        showConnectionPopup();
      }
    }, manualStopBuffer);
    // Also track in main timeouts array for proper cleanup
    timeoutsRef.current.push(connectionTimeouts.current.manualStop);

    // Snapshot which reels haven't stopped yet for animation suppression
    try {
      const totalReels = reelRefs.current ? reelRefs.current.length : 0;
      const snapshot = new Set();
      for (let i = 0; i < totalReels; i++) {
        if (!stoppedReelsRef.current.has(i)) {
          snapshot.add(i);
        }
      }
      manualStopTargetReelsRef.current = snapshot;
    } catch (e) {}

    // Stop tension animations and sounds
    soundManager.stop(SOUND_NAME.tension);
    // Don't stop shake animation here - let it play until spin completes
    const tensionReelsArray = tensionReelRefs.current;
    for (let i = 0; i < tensionReelsArray.length; i++) {
      const tensionReel = tensionReelsArray[i];
      if (tensionReel) {
        tensionReel.forceStopTension();
      }
    }

    // Undim all reels on manual stop
    for (let i = 0; i < reelRefs.current.length; i++) {
      reelRefs.current[i]?.undimReel();
    }

    // Check if autoplay is active BEFORE clearing tracking state
    const currentAutoPlayActive = currentState.settings.autoPlayActive;

    // CRITICAL: Don't clear stoppedReelsRef during autoplay - autoplay needs it for natural stopping
    // Only clear tracking state if autoplay is NOT active (manual spin scenario)
    if (!currentAutoPlayActive) {
      // Reset all tracking state for manual spins
      stoppedReelsRef.current.clear();
      lastForceStoppedReelRef.current = -1;
      activeTensionAnimationsRef.current.clear();
      tensionAnimationStartTimesRef.current.clear();
      dimmedReelsRef.current.clear();
    } else {
      // During autoplay, only clear tension-related state, preserve stoppedReelsRef
      activeTensionAnimationsRef.current.clear();
      tensionAnimationStartTimesRef.current.clear();
      dimmedReelsRef.current.clear();
    }

    dispatch(manualSpinStop());

    // Only stop autoplay if it's NOT active (manual spin scenario)
    // If autoplay IS active, let it continue to next spin after manual stop
    if (!currentAutoPlayActive) {
      dispatch(stopAutoPlay());
    }

    if (spacebarTurboActive) {
      setSpacebarTurboActive(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, spacebarTurboActive, showConnectionPopup]);

  /**
   * Initiates a new spin - main entry point for all spin types (manual, autoplay, turbo).
   *
   * Complete spin initialization flow:
   * - Validates state guards (UI visibility, connection popup, win animations)
   * - Rate limits to prevent spam (75ms minimum between spins)
   * - Resets all tracking state (timeouts, tension, animations, reel states)
   * - Resets counter animation finished flag for new spin
   * - Clears connection timeouts and popups
   * - Force-resets internal state of all reel controllers
   * - Checks balance and triggers fake spin if insufficient credits
   * - Dispatches API call (rgsPlay or triggerFakeSpin) with optional options
   * - Starts appropriate spin type based on current speed:
   *   * Turbo: All reels start simultaneously, triggers button rotation animation
   *   * Regular: Sequential reel starts
   * - Sets up emergency timeout fallback (5 seconds) to prevent stuck spins
   *
   * @param {Object} options - Optional parameters to pass to rgsPlay (e.g., { purchasedFeature: "bonus_buy" })
   *
   * Called by: manual clicks, autoplay, turbo mode, and first spin initialization.
   */
  const handleSpin = useCallback(
    (options = {}) => {
      const currentState = store.getState();
      if (currentState.userInterface.userInterfaceHidden) {
        return;
      }

      if (currentState.userInterface.connectionPopupActive) {
        return;
      }

      if (currentState.animations.winAnimationsPlaying) {
        return;
      }

      // Block new spin if there's an active win counter that hasn't finished
      // Prevents skipping wins via fast spacebar during win processing
      if (
        currentState.animations.counterAnimationStarted &&
        !currentState.animations.counterAnimationFinished
      ) {
        return;
      }

      // Rate limiting (75ms minimum between spins)
      const now = Date.now();
      const timeSinceLastSpin = now - (handleSpin.lastCall || 0);
      if (timeSinceLastSpin < 75) {
        return;
      }
      handleSpin.lastCall = now;

      if (isSpinning) {
        // Allow tension skip via click/tap during active tension
        if (pendingTensionRef.current) {
          handleManualStop();
        }
        return;
      }

      // Reset all state for new spin
      dispatch(setCounterAnimationFinished(false));
      clearConnectionTimeouts();
      const uiState = store.getState();
      if (uiState.userInterface.connectionPopupActive) {
        dispatch(closeConnectionPopup());
      }
      suppressLandingAnimationsRef.current = false;
      suppressLandingAnimationsPerReelRef.current.clear();
      manualStopTargetReelsRef.current.clear();
      clearAllTimeouts(); // ⚠️ This clears ALL timeouts including reel start timeouts if called again
      stopTriggeredRef.current = false;
      pendingManualStopRef.current = false;
      currentSpinMethodRef.current = null;
      stoppedReelsRef.current.clear();
      lastForceStoppedReelRef.current = -1;
      pendingTensionRef.current = null;
      tensionReels.current = new Set();
      activeTensionAnimationsRef.current.clear();
      tensionAnimationStartTimesRef.current.clear();
      dimmedReelsRef.current.clear();
      tensionEffectActiveRef.current = false; // Reset effect flag for new spin

      // Undim all reels at start of new spin
      for (let i = 0; i < reelRefs.current.length; i++) {
        reelRefs.current[i]?.undimReel();
      }

      // Reset responsible gambling check flag for new spin
      if (FEATURES_CONFIG.RESPONSIBLE_GAMBLING.ENABLED) {
        responsibleGamblingCheckedRef.current = false;
      }

      // Force-reset internal state of all reel controllers
      const reels = reelRefs.current;
      for (let i = 0; i < reels.length; i++) {
        const controller = reels[i]?.getReelController();
        if (controller) {
          controller.isStopping = false;
          controller.hasStopped = false;
          controller.hasTensionThisSpin = false;
        }
      }

      // Emergency fallback timeout (15 seconds)
      // Increased to account for tension animations which can extend spin duration significantly
      // Max tension duration: 3 reels × 2000ms = 6000ms + normal spin time (~3s) = ~9s total
      const emergencyStopTimeout = setTimeout(() => {
        // Remove this timeout from tracking since it fired naturally
        const idx = timeoutsRef.current.indexOf(emergencyStopTimeout);
        if (idx > -1) {
          timeoutsRef.current.splice(idx, 1);
        }
        if (store.getState().spin.isSpinning) {
          handleManualStop();
        }
      }, 15000);
      timeoutsRef.current.push(emergencyStopTimeout);

      // Always send API request - let API handle insufficient credits
      spinStartTimeRef.current = Date.now();
      dispatch(rgsPlay(options));

      // Start appropriate spin type based on current speed
      const currentAutoPlaySpeed = store.getState().settings.autoPlaySpeed;
      const reelsForSpin = reelRefs.current;

      // Trigger theme-appropriate animation on every spin start (manual, autoplay, turbo)
      if (playButtonRef.current) {
        if (Settings.APP.UI_THEME === "secondary") {
          // Secondary theme: rotation animation only (no scale press)
          createPlayButtonRotationAnimation(playButtonRef.current);
        } else {
          // Primary theme: scale press animation
          triggerScalePress(playButtonRef.current);
        }
      }

      if (currentAutoPlaySpeed === "turbo") {
        currentSpinMethodRef.current = "turbo";
      } else {
        currentSpinMethodRef.current = "regular";
      }

      // Unified spin start: speed config controls wind-up, stagger, and spin speed
      // Turbo: SKIP_WIND_UP=true, REEL_START_DELAYS=[0,0,0,0,0] → all start simultaneously
      // Quick: SKIP_WIND_UP=true, staggered delays → sequential start, no wind-up
      // Auto: wind-up + staggered delays → sequential start with wind-up
      // Check speed config for per-mode settings
        const speedConfig =
          FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[
            currentAutoPlaySpeed?.toUpperCase()
          ] || FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO;
        const reelStartDelay =
          speedConfig.REEL_START_DELAY !== undefined
            ? speedConfig.REEL_START_DELAY
            : FEATURES_CONFIG.SPIN.REEL_START_DELAY;
        const reelStartDelays =
          speedConfig.REEL_START_DELAYS ||
          FEATURES_CONFIG.SPIN.REEL_START_DELAYS;
        const skipWindUp = speedConfig.SKIP_WIND_UP || false;

        // Sequential or simultaneous start based on speed mode
        for (let i = 0; i < reelsForSpin.length; i++) {
          // Use non-linear per-reel delays if available, otherwise fall back to linear
          const delay = reelStartDelays
            ? reelStartDelays[i] || 0
            : i * reelStartDelay;
          if (delay === 0) {
            if (skipWindUp) {
              reelsForSpin[i]?.startSpinNoWindUp();
            } else {
              reelsForSpin[i]?.startSpin();
            }
          } else {
            const timeoutId = setTimeout(() => {
              const idx = timeoutsRef.current.indexOf(timeoutId);
              if (idx > -1) {
                timeoutsRef.current.splice(idx, 1);
              }
              if (skipWindUp) {
                reelsForSpin[i]?.startSpinNoWindUp();
              } else {
                reelsForSpin[i]?.startSpin();
              }
            }, delay);
            timeoutsRef.current.push(timeoutId);
          }
        }

        // After all reels finish wind-up, check if manual stop is pending
        // Calculate total time: (last reel start delay) + (wind-up duration) + (safety buffer)
        const windUpDuration = FEATURES_CONFIG.SPIN.WIND_UP_DURATION;
        const lastReelStartDelay = (reelsForSpin.length - 1) * reelStartDelay;
        const totalWindUpTime = lastReelStartDelay + windUpDuration;

        // If wind-up is disabled or skipped (quick mode), check immediately
        const checkDelay =
          windUpDuration === 0 || skipWindUp
            ? 0
            : totalWindUpTime + FEATURES_CONFIG.SPIN.WIND_UP_CHECK_BUFFER;

        const checkPendingStopTimeout = setTimeout(() => {
          // Remove this timeout from tracking since it fired naturally
          const idx = timeoutsRef.current.indexOf(checkPendingStopTimeout);
          if (idx > -1) {
            timeoutsRef.current.splice(idx, 1);
          }

          // If manual stop was pressed during wind-up, execute it now
          if (pendingManualStopRef.current && stopTriggeredRef.current) {
            // Track manual stops for turbo suggestion feature (only if feature is enabled)
            if (FEATURES_CONFIG.TURBO_SUGGESTION.ENABLED) {
              const settingsState = store.getState().settings;
              if (settingsState.manualStopTrackingEnabled) {
                dispatch(incrementManualStopCount());
                if (settingsState.manualStopCount >= 4) {
                  dispatch(setTurboSuggestionPending(true));
                }
              }
            }

            // Set connection timeout for manual stop
            const manualStopBuffer =
              CONNECTION_POPUP_TIMEOUTS.MANUAL_STOP_BUFFER;
            connectionTimeouts.current.manualStop = setTimeout(() => {
              const recheckState = store.getState();
              const stillNoData =
                recheckState.spin.status !== "succeeded" ||
                !recheckState.game.currentRound?.reels?.length;

              if (
                stillNoData &&
                recheckState.spin.manualStop &&
                recheckState.spin.isSpinning
              ) {
                showConnectionPopup();
              }
            }, manualStopBuffer);
            // Also track in main timeouts array for proper cleanup
            timeoutsRef.current.push(connectionTimeouts.current.manualStop);

            // Snapshot which reels haven't stopped yet for animation suppression
            try {
              const totalReels = reelRefs.current ? reelRefs.current.length : 0;
              const snapshot = new Set();
              for (let i = 0; i < totalReels; i++) {
                if (!stoppedReelsRef.current.has(i)) {
                  snapshot.add(i);
                }
              }
              manualStopTargetReelsRef.current = snapshot;
            } catch (e) {}

            // Stop tension animations and sounds
            soundManager.stop(SOUND_NAME.tension);
            // Don't stop shake animation here - let it play until spin completes
            const tensionReelsArray = tensionReelRefs.current;
            for (let i = 0; i < tensionReelsArray.length; i++) {
              const tensionReel = tensionReelsArray[i];
              if (tensionReel) {
                tensionReel.forceStopTension();
              }
            }

            // Undim all reels on manual stop
            for (let i = 0; i < reelRefs.current.length; i++) {
              reelRefs.current[i]?.undimReel();
            }

            // Reset all tracking state
            stoppedReelsRef.current.clear();
            lastForceStoppedReelRef.current = -1;
            activeTensionAnimationsRef.current.clear();
            tensionAnimationStartTimesRef.current.clear();
            dimmedReelsRef.current.clear();

            dispatch(manualSpinStop());

            // Only stop autoplay if it's NOT active (manual spin scenario)
            const currentAutoPlayActive =
              store.getState().settings.autoPlayActive;
            if (!currentAutoPlayActive) {
              dispatch(stopAutoPlay());
            }

            if (spacebarTurboActive) {
              setSpacebarTurboActive(false);
            }
          }
        }, checkDelay);
        timeoutsRef.current.push(checkPendingStopTimeout);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      dispatch,
      clearAllTimeouts,
      clearConnectionTimeouts,
      handleManualStop,
      isSpinning,

      reelRefs,
      playButtonRef,
      showConnectionPopup,
      spacebarTurboActive,
      tensionReelRefs,
    ],
  );

  // ============================================================================
  // TURBO MODE HANDLERS (Spacebar-activated)
  // ============================================================================

  /**
   * Activates turbo mode when spacebar/button is held (after 200ms hold duration).
   *
   * Activation flow:
   * - Validates state guards (UI visibility, win animations, autoplay not active)
   * - Stops current spin if one is in progress (via handleManualStop)
   * - Stores previous speed for restoration when turbo deactivates
   * - Sets turbo speed in Redux store
   * - Activates turbo state (spacebarTurboActive = true)
   * - Resets spinComplete flag to allow first turbo spin
   *
   * Note: Can be activated during counter animations - continuous effect handles waiting.
   * The actual first spin is triggered by the Turbo Activation Effect.
   */
  const startSpacebarTurbo = useCallback(() => {
    const currentState = store.getState();
    if (currentState.userInterface.userInterfaceHidden) {
      return;
    }

    // Allow tension skip even during autoplay — spacebar/click skips current tension reel
    if (pendingTensionRef.current && isSpinning) {
      handleManualStop();
      return;
    }

    // CRITICAL: Block turbo activation during autoplay
    if (currentState.settings.autoPlayActive) {
      return;
    }

    if (currentState.animations.winAnimationsPlaying) {
      return;
    }

    // Allow turbo to be activated even during counter animation
    // The continuous effect will handle waiting for counter to finish

    if (autoPlayActive || spacebarTurboActive) {
      return;
    }

    if (isSpinning) {
      handleManualStop();
      return;
    }

    if (autoPlaySpeed !== "turbo") {
      previousSpeedRef.current = autoPlaySpeed;
    }

    setSpacebarTurboActive(true);

    if (!isSpinning && spinComplete) {
      dispatch(setAutoplaySpeed("turbo"));
    }

    dispatch(setSpinComplete(false));
  }, [
    dispatch,
    autoPlayActive,
    spacebarTurboActive,
    isSpinning,
    spinComplete,
    autoPlaySpeed,
    handleManualStop,
  ]);

  /**
   * Deactivates turbo mode when spacebar/button is released.
   *
   * Deactivation flow:
   * - Validates state guards (UI visibility, autoplay not active)
   * - Clears all pending turbo activation flags
   * - Clears all tracked timeouts
   * - Sets turbo deactivating flag to prevent race conditions
   * - Deactivates turbo state (spacebarTurboActive = false)
   *
   * Speed restoration logic:
   * - If spin in progress: Defers speed restoration (sets turboPendingStopRef)
   * - If no spin active: Immediately restores previous speed
   * - Clears deactivating flag after 150ms delay
   *
   * The Spin Complete Cleanup Effect handles pending speed changes.
   */
  const stopSpacebarTurbo = useCallback(() => {
    const currentState = store.getState();
    if (currentState.userInterface.userInterfaceHidden) {
      return;
    }

    if (autoPlayActive) {
      return;
    }

    if (turboPendingActivationRef.current) {
      turboPendingActivationRef.current = false;
    }

    if (!spacebarTurboActive) {
      return;
    }

    turboDeactivatingRef.current = true;
    setSpacebarTurboActive(false);
    clearAllTimeouts();

    if (isSpinning && autoPlaySpeed === "turbo") {
      turboPendingStopRef.current = true;
    } else {
      dispatch(setAutoplaySpeed(previousSpeedRef.current));
      turboPendingStopRef.current = false;

      setTimeout(() => {
        turboDeactivatingRef.current = false;
      }, 150);
    }
  }, [
    dispatch,
    autoPlayActive,
    spacebarTurboActive,
    clearAllTimeouts,
    autoPlaySpeed,
    isSpinning,
  ]);

  // ============================================================================
  // EFFECTS - TURBO MODE
  // ============================================================================

  /**
   * Turbo Activation Effect
   *
   * Handles the first turbo spin when spacebar turbo mode is activated.
   *
   * Flow:
   * - Rate limits activation attempts (tracks last activation time)
   * - Reads current state directly from store to avoid stale closures
   * - Validates conditions (turbo active, not spinning, autoplay not active, not deactivating)
   * - Sets turbo speed in Redux if not already set
   * - Checks balance before starting spin
   * - Triggers first turbo spin via handleSpin (which includes rotation animation)
   *
   * Only handles the FIRST spin - subsequent spins are managed by Turbo Continuous Effect.
   */
  useEffect(() => {
    const now = Date.now();
    lastActionRef.current.turboActivation = now;

    // Read current state to avoid circular dependencies
    const currentState = store.getState();
    const currentIsSpinning = currentState.spin.isSpinning;
    const currentAutoPlayActive = currentState.settings.autoPlayActive;
    const currentAutoPlaySpeed = currentState.settings.autoPlaySpeed;
    const currentSpinComplete = currentState.spin.spinComplete;
    if (turboDeactivatingRef.current) {
      return;
    }

    if (!spacebarTurboActive || currentIsSpinning || currentAutoPlayActive) {
      return;
    }

    // Set turbo speed if not already set
    if (currentAutoPlaySpeed !== "turbo") {
      dispatch(setAutoplaySpeed("turbo"));
      return;
    }

    // Only start first turbo spin, continuous effect handles subsequent
    if (currentSpinComplete) {
      return;
    }

    // Allow turbo regardless of balance - API will handle insufficient credits
    handleSpin();
  }, [spacebarTurboActive, dispatch, stopSpacebarTurbo, handleSpin]);

  /**
   * Turbo Continuous Spinning Effect
   *
   * Handles all turbo spins after the first one (which is handled by Turbo Activation Effect).
   *
   * Flow for non-winning rounds:
   * - Rate limits to prevent rapid multiple spins (200ms minimum)
   * - Validates conditions (turbo active, spin complete, no autoplay, no manual stop, no win animations)
   * - Checks balance before spinning
   * - Waits 75ms delay for visual pacing
   * - Re-checks conditions before spinning (state may have changed)
   * - Triggers next spin via handleSpin (includes rotation animation)
   *
   * Flow for winning rounds:
   * - Waits for counter animation to complete (counterAnimationFinished = true)
   * - Validates all conditions again before spinning
   * - Triggers next spin immediately (no delay - counter display already provides timing)
   *
   * Rotation animation triggers inside handleSpin when reels start for perfect synchronization.
   * Handles cleanup of timeouts properly to prevent accumulation.
   */
  useEffect(() => {
    const now = Date.now();

    // Rate limiting to prevent rapid multiple turbo spins
    const timeSinceLastTurbo =
      now - (lastActionRef.current.turboContinuous || 0);
    if (timeSinceLastTurbo < 200) {
      return;
    }

    lastActionRef.current.turboContinuous = now;

    // Read current state to avoid circular dependencies
    const currentState = store.getState();
    const currentAutoPlayActive = currentState.settings.autoPlayActive;
    const currentManualStop = currentState.spin.manualStop;
    const currentSpinComplete = currentState.spin.spinComplete;
    const currentAutoPlaySpeed = currentState.settings.autoPlaySpeed;
    const currentCounterAnimationFinished =
      currentState.animations.counterAnimationFinished;
    const currentWinAnimationsPlaying =
      currentState.animations.winAnimationsPlaying;

    // CRITICAL: Always check autoplay state - turbo should NEVER run during autoplay
    if (currentAutoPlayActive) {
      // Force stop turbo if somehow still active during autoplay
      if (spacebarTurboActive) {
        setSpacebarTurboActive(false);
        dispatch(setAutoplaySpeed("auto"));
      }
      return;
    }

    // Block turbo spins if popups are active
    const currentErrorPopupActive = currentState.userInterface.errorPopupActive;
    const currentInfoPopupActive = currentState.userInterface.infoPopupActive;

    if (
      !spacebarTurboActive ||
      currentManualStop ||
      !currentSpinComplete ||
      currentWinAnimationsPlaying ||
      currentErrorPopupActive ||
      currentInfoPopupActive
    ) {
      return;
    }

    // Wait for activation effect to set turbo speed
    if (currentAutoPlaySpeed !== "turbo") {
      return;
    }

    // Allow turbo continuous regardless of balance - API will handle insufficient credits
    const currentRoundWin = currentState.game.currentRound?.win;

    if (!currentRoundWin) {
      const timeoutId = setTimeout(() => {
        // Remove this timeout from tracking since it fired naturally
        const idx = timeoutsRef.current.indexOf(timeoutId);
        if (idx > -1) {
          timeoutsRef.current.splice(idx, 1);
        }

        // Re-check conditions before spinning
        const recheckState = store.getState();
        const recheckSpacebarTurboActive = spacebarTurboActive;

        // CRITICAL: Double-check autoplay state before spinning
        if (recheckState.settings.autoPlayActive) {
          if (recheckSpacebarTurboActive) {
            setSpacebarTurboActive(false);
            dispatch(setAutoplaySpeed("auto"));
          }
          return;
        }

        if (
          recheckSpacebarTurboActive &&
          !recheckState.settings.autoPlayActive &&
          !recheckState.spin.manualStop &&
          recheckState.settings.autoPlaySpeed === "turbo" &&
          !turboDeactivatingRef.current &&
          !recheckState.userInterface.errorPopupActive &&
          !recheckState.userInterface.infoPopupActive
        ) {
          // Rotation animation now triggers inside handleSpin when reels start
          handleSpin();
        }
      }, FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO.SPIN_DELAY); // Delay between turbo spins (allows landing animations to complete)

      timeoutsRef.current.push(timeoutId);

      // Return cleanup function at effect level
      return () => {
        clearTimeout(timeoutId);
        const index = timeoutsRef.current.indexOf(timeoutId);
        if (index > -1) {
          timeoutsRef.current.splice(index, 1);
        }
      };
    } else {
      // Wait for all animations to complete before next turbo spin
      if (currentCounterAnimationFinished) {
        // CRITICAL: Double-check autoplay state before spinning
        const finalCheckState = store.getState();
        if (finalCheckState.settings.autoPlayActive) {
          if (spacebarTurboActive) {
            setSpacebarTurboActive(false);
            dispatch(setAutoplaySpeed("auto"));
          }
          return;
        }

        // Re-check conditions before spinning
        // No additional delay needed - 800ms counter display is already enough
        if (
          spacebarTurboActive &&
          !currentAutoPlayActive &&
          !currentManualStop &&
          currentAutoPlaySpeed === "turbo" &&
          !turboDeactivatingRef.current &&
          !currentWinAnimationsPlaying &&
          !currentErrorPopupActive &&
          !currentInfoPopupActive
        ) {
          // Rotation animation now triggers inside handleSpin when reels start
          handleSpin();
        }
      }

      // Return empty cleanup for win branch
      return () => {};
    }
  }, [
    spacebarTurboActive,
    spinComplete,
    counterAnimationFinished,
    winAnimationsPlaying,
    dispatch,
    handleSpin,
  ]);

  /**
   * Turbo Stop on Win Animation Start Effect
   *
   * Automatically stops turbo mode when BIG win animations begin (BIG_WIN, MEGA_WIN, SUPER_WIN, EPIC_WIN).
   *
   * Important distinction:
   * - Counter animations (small wins): Turbo continues and waits for counter to finish
   * - Win animations (big celebrations): Turbo stops completely, user must restart manually
   *
   * Implementation:
   * - Tracks previous animation state to detect START transition (false → true)
   * - Only stops turbo on the transition to avoid stopping on state resets
   * - Calls stopSpacebarTurbo() which handles cleanup and speed restoration
   *
   * This ensures users can't skip big win celebrations with turbo mode.
   */
  const previousAnimationFlagsRef = useRef({
    counterAnimationStarted: false,
    winAnimationsPlaying: false,
  });

  useEffect(() => {
    const winAnimationsJustStarted =
      winAnimationsPlaying &&
      !previousAnimationFlagsRef.current.winAnimationsPlaying;

    // Only stop turbo for win animations, not for counter animations
    // Counter animations will complete and turbo will continue automatically
    if (winAnimationsJustStarted && spacebarTurboActive) {
      stopSpacebarTurbo();
    }

    previousAnimationFlagsRef.current = {
      counterAnimationStarted,
      winAnimationsPlaying,
    };
  }, [
    counterAnimationStarted,
    winAnimationsPlaying,
    spacebarTurboActive,
    stopSpacebarTurbo,
  ]);

  /**
   * Safety Effect: Stop turbo when error/info popups appear or free rounds complete
   *
   * Prevents turbo spins from continuing when critical popups are displayed.
   * Similar to how turbo stops for win animations, this ensures users can't
   * bypass important messages (like free rounds ending) by holding spacebar.
   *
   * CRITICAL: Also stops turbo when free rounds completion is pending, BEFORE popup opens.
   * This prevents the user from triggering a spin past the last free round during the
   * animation wait period (between last round completing and popup showing).
   *
   * Tracks popup state transitions to detect when they open (false → true).
   * Only stops turbo on the transition to avoid stopping on state resets.
   */
  const previousPopupFlagsRef = useRef({
    errorPopupActive: false,
    infoPopupActive: false,
    freeRoundsCompletionPending: false,
  });

  useEffect(() => {
    const errorPopupJustOpened =
      errorPopupActive && !previousPopupFlagsRef.current.errorPopupActive;
    const infoPopupJustOpened =
      infoPopupActive && !previousPopupFlagsRef.current.infoPopupActive;
    const freeRoundsCompletionJustStarted =
      freeRoundsCompletionPending &&
      !previousPopupFlagsRef.current.freeRoundsCompletionPending;

    // Stop turbo if any popup just opened OR free rounds completion just started
    if (
      (errorPopupJustOpened ||
        infoPopupJustOpened ||
        freeRoundsCompletionJustStarted) &&
      spacebarTurboActive
    ) {
      stopSpacebarTurbo();
    }

    previousPopupFlagsRef.current = {
      errorPopupActive,
      infoPopupActive,
      freeRoundsCompletionPending,
    };
  }, [
    errorPopupActive,
    infoPopupActive,
    freeRoundsCompletionPending,
    spacebarTurboActive,
    stopSpacebarTurbo,
  ]);

  /**
   * Safety Effect: Force stop turbo if autoplay becomes active
   *
   * This is a critical safety net to prevent turbo from running during autoplay.
   * If turbo somehow becomes active while autoplay is active (race condition, edge case),
   * this effect will immediately stop it.
   */
  useEffect(() => {
    if (autoPlayActive && spacebarTurboActive) {
      setSpacebarTurboActive(false);
      const currentState = store.getState();
      if (currentState.settings.autoPlaySpeed === "turbo") {
        dispatch(setAutoplaySpeed("auto"));
      }
    }
  }, [autoPlayActive, spacebarTurboActive, dispatch]);

  // ============================================================================
  // EFFECTS - AUTOPLAY MODE
  // ============================================================================

  /**
   * Autoplay Continuous Spinning Effect
   *
   * Manages continuous autoplay spins with proper timeout tracking and state management.
   *
   * Critical synchronization:
   * - Waits for ALL reels to mechanically stop (stoppedReelsRef.size === totalReels)
   * - Waits for ALL timeouts to clear (timeoutsRef.length === 0)
   * - Uses autoplayProcessingRef flag to prevent duplicate timeout creation
   *
   * Flow for non-winning rounds:
   * - Validates conditions (autoplay active, spin complete, app active, menu inactive)
   * - Checks balance and remaining spins
   * - Creates timeout with speed-specific delay (auto/quick/turbo)
   * - Decrements remaining spins and triggers next spin
   * - Properly tracks and cleans up timeout
   *
   * Flow for winning rounds:
   * - Waits for counter animation to complete (counterAnimationFinished = true)
   * - Prevents race condition on fresh autoplay initialization
   * - Decrements remaining spins and triggers next spin
   *
   * Handles pause/resume on app inactivity and menu states.
   */
  useEffect(() => {
    const {
      autoPlayActive,
      spinComplete,
      remainingAutoSpins,
      balance,
      counterAnimationFinished,
      // autoPlaySpeed removed - read directly from store when needed
      winAnimationsPlaying,
      currentRound,
      menuActive,
      holdAndWinActive,
      lastCommand,
      flowState,
      stopOnAnyWin,
      stopOnBonusGame,
      stopOnSingleWinExceeds,
      stopOnBalanceIncreases,
      stopOnBalanceDecreases,
      singleWinExceedsAmount,
      balanceIncreasesAmount,
      balanceDecreasesAmount,
      errorPopupActive,
      infoPopupActive,
      freeRoundsCompletionPending,
    } = autoplayState;

    if (!autoPlayActive || !spinComplete) {
      // Reset flag only when autoplay stops (not when spin starts)
      // Flag persists until timeout fires to prevent duplicate timeouts
      if (!autoPlayActive && autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
        autoplayStartBalanceRef.current = null; // Reset start balance when autoplay stops
      }
      return;
    }

    // Reset processing flag when spin completes (before scheduling next spin)
    if (autoplayProcessingRef.current) {
      autoplayProcessingRef.current = false;
    }

    // Capture starting balance on first spin of autoplay session
    if (autoplayStartBalanceRef.current === null) {
      autoplayStartBalanceRef.current = balance;
    }

    // Pause when app/tab is inactive
    if (!isActive) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return;
    }

    // Prevent duplicate processing
    if (autoplayProcessingRef.current) {
      return;
    }

    // Wait for win animations to complete
    if (winAnimationsPlaying) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return;
    }

    // Pause autoplay when error popup or info popup is active
    // This allows autoplay to resume after popups close (e.g., error 205 free rounds flow)
    if (errorPopupActive || infoPopupActive) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return; // Pause, don't stop
    }

    // Pause when free rounds completion flow is active
    // This prevents autoplay from continuing past the completion popups
    // Free rounds themselves continue in autoplay, but completion requires user interaction
    if (freeRoundsCompletionPending) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return; // Pause, don't stop
    }

    // Pause when menu is active
    if (menuActive) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return;
    }

    // Pause when Hold and Win feature is active OR when trigger is detected
    // Use universal featureState if available, fall back to legacy flowState
    // This pauses immediately when trigger is detected (before intro dialog), not just when feature is active
    const isHoldAndWinTrigger =
      FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
      ((featureState &&
        featureState.type === "holdAndWin" &&
        featureState.phase === "intro") ||
        (lastCommand === "spin" && flowState === "respin"));

    // Check responsible gambling for bonus game BEFORE pausing for Hold & Win
    // This ensures "Stop on Bonus Game" works correctly
    if (
      FEATURES_CONFIG.RESPONSIBLE_GAMBLING.ENABLED &&
      !responsibleGamblingCheckedRef.current &&
      (holdAndWinActive || isHoldAndWinTrigger)
    ) {
      const hasBonusGameTrigger = detectHoldAndWinTrigger(currentRound);
      if (stopOnBonusGame && hasBonusGameTrigger) {
        responsibleGamblingCheckedRef.current = true;
        dispatch(stopAutoPlay());
        resetResponsibleGamblingSettings();
        autoplayProcessingRef.current = false;
        autoplayStartBalanceRef.current = null;
        return;
      }
    }

    // Pause autoplay during Hold and Win (if not stopped by responsible gambling)
    if (holdAndWinActive || isHoldAndWinTrigger) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return;
    }

    // Pause autoplay during Free Spin trigger
    const isFreeSpinTrigger =
      FEATURES_CONFIG.FREE_SPINS.ENABLED &&
      featureState &&
      featureState.type === "freeSpins" &&
      featureState.phase === "intro";

    if (isFreeSpinTrigger) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return;
    }

    autoplayProcessingRef.current = true;

    // Allow autoplay to continue regardless of balance - API will handle insufficient credits
    if (remainingAutoSpins <= 0 && remainingAutoSpins !== Infinity) {
      dispatch(stopAutoPlay());
      autoplayProcessingRef.current = false;
      return;
    }

    const currentRoundWin = currentRound?.win;

    // Check responsible gambling conditions
    // Check immediately when spin completes, before scheduling next spin
    if (
      FEATURES_CONFIG.RESPONSIBLE_GAMBLING.ENABLED &&
      !responsibleGamblingCheckedRef.current
    ) {
      // 1. Stop on any win
      if (stopOnAnyWin && currentRoundWin && currentRoundWin > 0) {
        responsibleGamblingCheckedRef.current = true;
        dispatch(stopAutoPlay());
        resetResponsibleGamblingSettings();
        autoplayProcessingRef.current = false;
        autoplayStartBalanceRef.current = null;
        return;
      }

      // 2. Stop on bonus game (6+ coins detected) - fallback check
      // Primary check is above before Hold & Win pause
      const hasBonusGameTrigger = detectHoldAndWinTrigger(currentRound);
      if (stopOnBonusGame && hasBonusGameTrigger) {
        responsibleGamblingCheckedRef.current = true;
        dispatch(stopAutoPlay());
        resetResponsibleGamblingSettings();
        autoplayProcessingRef.current = false;
        autoplayStartBalanceRef.current = null;
        return;
      }

      // 3. Stop if single win exceeds amount
      if (
        stopOnSingleWinExceeds &&
        singleWinExceedsAmount &&
        currentRoundWin &&
        currentRoundWin > 0
      ) {
        // Convert display value (e.g., "1000.50") to wallet value (e.g., 100050)
        const winExceedsThresholdStr = parseAutoplayInputFromStore(
          singleWinExceedsAmount,
        );
        const winExceedsThreshold = Number(winExceedsThresholdStr);
        if (
          !isNaN(winExceedsThreshold) &&
          currentRoundWin > winExceedsThreshold
        ) {
          responsibleGamblingCheckedRef.current = true;
          dispatch(stopAutoPlay());
          resetResponsibleGamblingSettings();
          autoplayProcessingRef.current = false;
          autoplayStartBalanceRef.current = null;
          return;
        }
      }

      // 4. Stop if balance increases by amount
      if (
        stopOnBalanceIncreases &&
        balanceIncreasesAmount &&
        autoplayStartBalanceRef.current !== null
      ) {
        // Convert display value (e.g., "1000.50") to wallet value (e.g., 100050)
        const increaseThresholdStr = parseAutoplayInputFromStore(
          balanceIncreasesAmount,
        );
        const increaseThreshold = Number(increaseThresholdStr);
        const balanceIncrease = balance - autoplayStartBalanceRef.current;
        if (!isNaN(increaseThreshold) && balanceIncrease >= increaseThreshold) {
          responsibleGamblingCheckedRef.current = true;
          dispatch(stopAutoPlay());
          resetResponsibleGamblingSettings();
          autoplayProcessingRef.current = false;
          autoplayStartBalanceRef.current = null;
          return;
        }
      }

      // 5. Stop if balance decreases by amount
      if (
        stopOnBalanceDecreases &&
        balanceDecreasesAmount &&
        autoplayStartBalanceRef.current !== null
      ) {
        // Convert display value (e.g., "1000.50") to wallet value (e.g., 100050)
        const decreaseThresholdStr = parseAutoplayInputFromStore(
          balanceDecreasesAmount,
        );
        const decreaseThreshold = Number(decreaseThresholdStr);
        const balanceDecrease = autoplayStartBalanceRef.current - balance;
        if (!isNaN(decreaseThreshold) && balanceDecrease >= decreaseThreshold) {
          responsibleGamblingCheckedRef.current = true;
          dispatch(stopAutoPlay());
          resetResponsibleGamblingSettings();
          autoplayProcessingRef.current = false;
          autoplayStartBalanceRef.current = null;
          return;
        }
      }

      responsibleGamblingCheckedRef.current = true;
    }

    if (!currentRoundWin) {
      // Read speed directly from store to avoid effect re-runs on speed changes
      const currentAutoPlaySpeed = store.getState().settings.autoPlaySpeed;

      const speedConfig =
        FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[
          currentAutoPlaySpeed?.toUpperCase()
        ] || FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO;

      // Use turbo spin delay for turbo speed to allow landing animations
      // For other speeds, use the configured AUTO_PLAY_DELAY
      const delay =
        currentAutoPlaySpeed === "turbo"
          ? FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO.SPIN_DELAY
          : speedConfig.AUTO_PLAY_DELAY;

      const timeoutId = setTimeout(() => {
        // Remove this timeout from tracking since it fired naturally
        const idx = timeoutsRef.current.indexOf(timeoutId);
        if (idx > -1) {
          timeoutsRef.current.splice(idx, 1);
        }

        dispatch(decrementRemainingAutoSpins());
        handleSpin();
        // Don't reset autoplayProcessingRef.current here - it will be reset when spinComplete becomes true
      }, delay);

      // Track timeout to prevent accumulation
      timeoutsRef.current.push(timeoutId);

      // Cleanup: Only clear timeout if it hasn't fired yet
      // Flag persists across effect re-runs to prevent duplicate timeouts
      return () => {
        clearTimeout(timeoutId);
        const index = timeoutsRef.current.indexOf(timeoutId);
        if (index > -1) {
          timeoutsRef.current.splice(index, 1);
        }
      };
    } else {
      // Winning round: Wait for counter animation to complete AND win animations to finish
      // Same check as spacebar turbo - prevents triggering next spin while win lines/animations are still playing
      const animationsComplete =
        counterAnimationFinished && !winAnimationsPlaying;

      if (animationsComplete) {
        // Prevent race condition on fresh autoplay initialization
        if (
          remainingAutoSpins === autoSpinCount &&
          !autoplayInitializedRef.current
        ) {
          autoplayProcessingRef.current = false;
          return;
        }

        // For all speeds (turbo, auto, quick), trigger immediately after counter finishes AND win animations complete
        // Counter animation already provides the visual pause, no additional delay needed
        dispatch(decrementRemainingAutoSpins());
        handleSpin();
        // Don't reset autoplayProcessingRef.current here - it will be reset when spinComplete becomes true
        return () => {};
      } else {
        autoplayProcessingRef.current = false;
        return () => {};
      }
    }
  }, [
    autoplayState,
    dispatch,
    autoSpinCount,
    handleSpin,
    isActive,
    reelRefs,
    resetResponsibleGamblingSettings,
    featureState,
  ]);

  /**
   * App Activity Change Effect
   *
   * Handles pause/resume when app/tab becomes inactive/active (browser tab switching).
   *
   * On pause (app becomes inactive):
   * - Sets pausedByInactivityRef flag
   * - Clears all timeouts (prevents autoplay/turbo from continuing)
   * - Stops tension sound effects
   * - Keeps reels spinning (doesn't interrupt active spin)
   *
   * On resume (app becomes active):
   * - Checks if was paused by inactivity
   * - If spin is active:
   *   * Reads fresh state directly from store (selectors may be stale after tab switch)
   *   * If API data available: Recomputes tension and stops reels with current data
   *   * If no API data: Resumes spin animations while waiting for API response
   *
   * Critical: Reads state directly from store on resume to avoid stale selector values.
   */
  useEffect(() => {
    if (!isActive) {
      pausedByInactivityRef.current = true;

      // Clear only non-stop-chain timeouts (autoplay delays, connection checks, etc.)
      // Stop-chain timeouts must be preserved — they fire while paused to set
      // isStopping on each reel, and resumeSpin's timing adjustment handles the rest.
      const stopTimeoutIds = new Set(reelStopTimeoutsRef.current.values());
      const timeouts = timeoutsRef.current;
      const preserved = [];
      for (let i = 0; i < timeouts.length; i++) {
        if (stopTimeoutIds.has(timeouts[i])) {
          preserved.push(timeouts[i]); // Keep stop-chain timeout
        } else {
          clearTimeout(timeouts[i]); // Kill autoplay/connection timeouts
        }
      }
      timeoutsRef.current = preserved;
      try {
        soundManager.stop(SOUND_NAME.tension);
      } catch {}

      // Pause all spinning reels — freezes them visually at current position
      if (isSpinning) {
        for (let i = 0; i < reelRefs.current.length; i++) {
          const controller = reelRefs.current[i]?.getReelController?.();
          if (controller && controller.isPlaying && !controller.hasStopped) {
            controller.pauseSpin();
          }
        }
      }

      // Pause all visible tension spines
      for (let i = 0; i < tensionReelRefs.current.length; i++) {
        if (tensionReelRefs.current[i]?.isTensionVisible()) {
          tensionReelRefs.current[i].pauseTension();
        }
      }
      return;
    }

    if (!pausedByInactivityRef.current) return;

    // Guard: Prevent multiple rapid calls when tab becomes active
    const now = Date.now();
    if (
      lastTabReturnTimeRef.current &&
      now - lastTabReturnTimeRef.current < 500
    ) {
      return;
    }
    lastTabReturnTimeRef.current = now;

    pausedByInactivityRef.current = false;

    if (isSpinning) {
      // Resume all paused reels — they pick up exactly where they froze
      for (let i = 0; i < reelRefs.current.length; i++) {
        const controller = reelRefs.current[i]?.getReelController?.();
        if (controller && controller._isPaused) {
          controller.resumeSpin();
        }
      }

      // Resume all paused tension spines
      for (let i = 0; i < tensionReelRefs.current.length; i++) {
        if (tensionReelRefs.current[i]?._isTensionPaused) {
          tensionReelRefs.current[i].resumeTension();
        }
      }

      // Edge case: API data arrived while paused but stopReels was never called
      // (user left tab before API responded). Stop-chain timeouts that were already
      // scheduled are preserved and will fire. This only handles the "never started" case.
      const freshState = store.getState();
      const round = freshState.game.currentRound;
      const reels = round?.reels;
      const hasData = Array.isArray(reels) && reels.length > 0;

      if (hasData) {
        // Check if any reels never received a stop command
        let hasUnstopped = false;
        for (let i = 0; i < reelRefs.current.length; i++) {
          const controller = reelRefs.current[i]?.getReelController?.();
          if (
            controller &&
            !controller.isStopping &&
            !controller.hasStopped &&
            controller.isPlaying
          ) {
            hasUnstopped = true;
            break;
          }
        }

        if (hasUnstopped) {
          const tensionToApply = pendingTensionRef.current
            ? pendingTensionRef.current.tensionStartReel
            : null;

          requestAnimationFrame(() => {
            stopReels(FEATURES_CONFIG.SPIN.STOP_DELAY_AUTO, tensionToApply);
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, clearAllTimeouts, dispatch, stopReels]);

  /**
   * Autoplay Initialization Effect
   *
   * Triggers the first spin when autoplay mode is activated.
   *
   * Conditions:
   * - Autoplay becomes active (autoPlayActive = true)
   * - Remaining spins equals total spins (indicates fresh start)
   * - Not already initialized (autoplayInitializedRef = false)
   *
   * Flow:
   * - Sets initialization flag to prevent duplicate first spins
   * - Decrements remaining spins count
   * - Triggers first spin via handleSpin
   *
   * Reset:
   * - Clears initialization flag when autoplay stops
   *
   * This ensures only ONE first spin is triggered, not multiple spins on rapid activation.
   */
  useEffect(() => {
    if (
      autoPlayActive &&
      remainingAutoSpins === autoSpinCount &&
      !autoplayInitializedRef.current
    ) {
      autoplayInitializedRef.current = true;
      dispatch(decrementRemainingAutoSpins());
      handleSpin();
    } else if (!autoPlayActive && autoplayInitializedRef.current) {
      autoplayInitializedRef.current = false;
    }
  }, [autoPlayActive, autoSpinCount, remainingAutoSpins, dispatch, handleSpin]);

  /**
   * Spin Complete Cleanup Effect
   *
   * Runs when a spin completes (spinComplete = true) to clean up state and handle pending actions.
   *
   * Uses cleanupInProgressRef guard to prevent race conditions from multiple rapid completions.
   *
   * Cleanup tasks:
   * - Clears pending manual stop flag
   * - Clears tension state (pendingTensionRef, tensionReels)
   * - Handles pending turbo activation (only if spacebar not held)
   * - Handles pending turbo stop (restores previous speed if turbo was stopped during spin)
   * - Applies pending autoplay speed changes
   * - Resets spin start time
   * - Clears active tension animations and timestamps
   *
   * Resets cleanupInProgressRef flag after completion via requestAnimationFrame.
   */
  useEffect(() => {
    if (spinComplete && !cleanupInProgressRef.current) {
      cleanupInProgressRef.current = true;

      pendingManualStopRef.current = false;

      // Clean up tension state
      if (pendingTensionRef.current) {
        pendingTensionRef.current = null;
        tensionReels.current = new Set();
      }
      // Stop tension effect and background event animation when spin completes
      if (tensionEffectActiveRef.current) {
        gameContainerRef?.current?.stopTensionEffect?.();
        tensionEffectActiveRef.current = false;
      }

      // Handle pending turbo activation (only if spacebar not held)
      if (
        turboPendingActivationRef.current &&
        !spacebarTurboActive &&
        !autoPlayActive
      ) {
        turboPendingActivationRef.current = false;
      }

      // Handle pending turbo stop
      if (turboPendingStopRef.current && autoPlaySpeed === "turbo") {
        dispatch(setAutoplaySpeed(previousSpeedRef.current));
        turboPendingStopRef.current = false;
        turboDeactivatingRef.current = false;
      }

      // Apply pending autoplay speed change
      if (pendingAutoPlaySpeed !== null) {
        dispatch(applyPendingAutoplaySpeed());
      }

      // Don't reset spinStartTimeRef here - it's needed by API Response Detection Effect
      // It will be reset when the next spin starts in handleSpin()
      activeTensionAnimationsRef.current.clear();
      tensionAnimationStartTimesRef.current.clear();
      turboTensionEjectedRef.current = false;

      requestAnimationFrame(() => {
        cleanupInProgressRef.current = false;
      });
    }
  }, [
    spinComplete,
    autoPlaySpeed,
    pendingAutoPlaySpeed,
    dispatch,
    spacebarTurboActive,
    autoPlayActive,
    gameContainerRef,
    clearAllTimeouts,
  ]);

  // ============================================================================
  // COMPONENT LIFECYCLE - UNMOUNT CLEANUP
  // ============================================================================

  /**
   * Component Unmount Cleanup Effect
   *
   * Ensures all timeouts, intervals, and subscriptions are properly cleaned up
   * when the component unmounts to prevent memory leaks.
   *
   * Critical for preventing lag in autoplay mode on Mac and other platforms.
   * Also prevents buildup when performing manual stops during autoplay.
   */
  useEffect(() => {
    // Capture refs at mount to avoid stale references in cleanup
    const gameContainer = gameContainerRef?.current;
    const stoppedReels = stoppedReelsRef.current;
    const activeTensionAnimations = activeTensionAnimationsRef.current;
    const tensionAnimationStartTimes = tensionAnimationStartTimesRef.current;
    const dimmedReels = dimmedReelsRef.current;

    return () => {
      // Clear all tracked timeouts
      clearAllTimeouts();

      // Clear connection timeouts
      clearConnectionTimeouts();
      clearManualStopTimeouts();

      // Stop all sounds
      try {
        soundManager.stop(SOUND_NAME.tension);
        soundManager.stop(SOUND_NAME.reel_spin);
      } catch (e) {}

      // Stop tension effect animation
      try {
        gameContainer?.stopTensionEffect?.();
      } catch (e) {}

      // Reset all refs to prevent stale references
      autoplayProcessingRef.current = false;
      autoplayInitializedRef.current = false;
      autoplayStartBalanceRef.current = null;
      pausedByInactivityRef.current = false;
      if (FEATURES_CONFIG.RESPONSIBLE_GAMBLING.ENABLED) {
        responsibleGamblingCheckedRef.current = false;
      }
      cleanupInProgressRef.current = false;
      isSubscribedRef.current = false;
      stopTriggeredRef.current = false;
      pendingManualStopRef.current = false;
      turboPendingStopRef.current = false;
      turboPendingActivationRef.current = false;
      turboDeactivatingRef.current = false;
      tensionEffectActiveRef.current = false;

      // Clear all Sets and Maps
      stoppedReels.clear();
      tensionReels.current.clear();
      activeTensionAnimations.clear();
      tensionAnimationStartTimes.clear();
      dimmedReels.clear();
      suppressLandingAnimationsPerReelRef.current.clear();
      manualStopTargetReelsRef.current.clear();

      // Clear tension state
      pendingTensionRef.current = null;
      currentSpinMethodRef.current = null;
      spinStartTimeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearAllTimeouts, clearConnectionTimeouts, clearManualStopTimeouts]);

  /**
   * Context Value Memoization
   *
   * Memoizes the context value to prevent unnecessary re-renders of consuming components.
   * Only recreates when dependencies change (handlers, turbo state, or suppression refs).
   *
   * Exposed API:
   * - handleSpin: Main spin initiation function
   * - handleManualStop: Manual stop handler
   * - startSpacebarTurbo: Turbo activation handler
   * - stopSpacebarTurbo: Turbo deactivation handler
   * - spacebarTurboActive: Current turbo state
   * - tensionReels: Set of reels in tension mode
   * - suppressLandingAnimationsRef: Flag to suppress all landing animations (turbo mode)
   * - suppressLandingAnimationsPerReelRef: Set of reel indices to suppress landing animations
   */
  const contextValue = useMemo(() => {
    return {
      handleSpin,
      handleManualStop,
      startSpacebarTurbo,
      stopSpacebarTurbo,
      spacebarTurboActive,
      tensionReels,
      pendingTensionRef, // Expose for tension skip in ButtonPlay
      turboTensionEjectedRef, // Expose to block held-spacebar tension skips
      restoreReels, // Expose restore function for error recovery
      // Expose landing suppression flags to hooks/components for animation optimization
      suppressLandingAnimationsRef,
      suppressLandingAnimationsPerReelRef,
    };
  }, [
    handleSpin,
    handleManualStop,
    startSpacebarTurbo,
    stopSpacebarTurbo,
    spacebarTurboActive,
    restoreReels,
    suppressLandingAnimationsRef,
    suppressLandingAnimationsPerReelRef,
  ]);

  return (
    <ReelControlContext.Provider value={contextValue}>
      {children}
    </ReelControlContext.Provider>
  );
};

// Custom hook for consuming ReelControlContext
export const useReelControl = () => {
  const context = useContext(ReelControlContext);
  if (!context) {
    throw new Error("useReelControl must be used within a ReelControlProvider");
  }
  return context;
};
